/**
 * API Client for Admin API
 * Handles authentication and request/response processing
 * Uses Cloudflare Access cookies for authentication (enforced at the edge)
 *
 * This client makes direct requests to the Cloudflare Worker API.
 * The Worker must have CORS headers configured to allow requests from the Pages site origin.
 * Authentication is handled by Cloudflare Access cookies, not service tokens.
 */

import { z } from 'zod'
import {
  MaTaxRegistrationResponseSchema,
  MaTaxMunicipalitiesResponseSchema,
  RocReportSchema,
  RocVersionSchema,
  RocVersionsResponseSchema,
  CreateRocVersionResponseSchema,
  MaTaxConfirmationsResponseSchema,
  MaTaxConfirmationSchema,
  OwnerEmailPreviewsResponseSchema,
  SendOwnerEmailsResponseSchema,
} from './ma-tax-schemas'
import type {
  UpdatePropertyRequest,
  UpdatePropertyExternalIdsRequest,
  AskPropertyQuestionRequest,
  UpdatePropertyEconomicsRequest,
  UpdateReservationExternalIdsRequest,
  MarkThreadReadRequest,
  ReassignMessageRequest,
  ListOrphanedMessagesRequest,
  UpdateAiSettingsRequest,
  UpsertUserRequest,
  InviteUserRequest,
  PatchUserRequest,
  ReportEmailHistoryQuery,
  ReingestPulseRequest,
  SuggestionFeedbackRequest,
  SendSuggestionMessageRequest,
  SendThankYouSuggestionRequest,
  MessageSuggestionStatus,
  StartOnboardingRequest,
  PropertyProfileSectionId,
  PropertyDocumentKind,
  MaTaxRegistration,
} from '@natalan/capehost-contracts'
import {
  PropertiesListResponseSchema,
  PropertyListItemSchema,
  GetPropertyResponseSchema,
  PropertyReservationsResponseSchema,
  ReservationSummarySchema,
  PropertyPricingCalendarResponseSchema,
  PropertyReviewsResponseSchema,
  GetPropertyExternalIdsResponseSchema,
  UpdatePropertyResponseSchema,
  UpdatePropertyExternalIdsResponseSchema,
  AdminPropertyFeaturesListResponseSchema,
  AdminPropertyFeatureUpdateResponseSchema,
  PropertyThreadsResponseSchema,
  PropertyThreadsMessagesResponseSchema,
  PropertyThreadsPageResponseSchema,
  ThreadsPageResponseSchema,
  AskPropertyQuestionResponseSchema,
  ListPropertyAskSessionsResponseSchema,
  GetPropertyAskSessionResponseSchema,
  GetPropertyKnowledgeThreadResponseSchema,
  GetPropertyEconomicsResponseSchema,
  UpdatePropertyEconomicsResponseSchema,
  GetReservationResponseSchema,
  GetReservationThreadResponseSchema,
  GetReservationExternalIdsResponseSchema,
  UpdateReservationExternalIdsResponseSchema,
  ThreadMessagesResponseSchema,
  MarkThreadReadResponseSchema,
  DismissThreadNeedsReplyResponseSchema,
  ReassignMessageResponseSchema,
  ListOrphanedMessagesResponseSchema,
  DeleteOrphanedMessageResponseSchema,
  ReprocessMessageResponseSchema,
  AdminStatusResponseSchema,
  UplistingSyncResponseSchema,
  UplistingReservationSyncResponseSchema,
  SeedPromptsMigrationResponseSchema,
  AiSettingsResponseSchema,
  UpdateAiSettingsResponseSchema,
  ListUsersResponseSchema,
  UpsertUserResponseSchema,
  InviteUserResponseSchema,
  ResendInviteResponseSchema,
  GetUserResponseSchema,
  PatchUserResponseSchema,
  DeleteUserResponseSchema,
  GetUserPropertiesResponseSchema,
  SetUserPropertiesResponseSchema,
  TriggerReportEmailResponseSchema,
  ReportEmailHistoryResponseSchema,
  DeleteReportEmailRecordResponseSchema,
  ReportEmailPreviewResponseSchema,
  ListNewslettersResponseSchema,
  NewsletterPreviewResponseSchema,
  NewsletterSendTestResponseSchema,
  NewsletterSendAllResponseSchema,
  ListPulseIngestRunsResponseSchema,
  ReingestPulseResponseSchema,
  GetMessageSuggestionsResponseSchema,
  SuggestionFeedbackResponseSchema,
  GetReviewSuggestionResponseSchema,
  RegenerateReviewSuggestionResponseSchema,
  GetThankYouSuggestionResponseSchema,
  RegenerateThankYouSuggestionResponseSchema,
  SendThankYouSuggestionResponseSchema,
  SendSuggestionMessageResponseSchema,
  RegenerateSuggestionResponseSchema,
  ListLogoAssetsResponseSchema,
  UploadImageResponseSchema,
  ReviewChaseDigestResponseSchema,
  ReviewChaseCheckoutTodayRowSchema,
  ReviewChasePendingRowSchema,
  ReviewChaseSkipReasonResponseSchema,
  GetReceiptResponseSchema,
  ListReceiptsResponseSchema,
  ReclassifyReceiptsResponseSchema,
  UnmatchedReceiptsResponseSchema,
  SecurityDepositAutomationSchema,
  SecurityDepositConfigResponseSchema,
  ReservationSecurityDepositResponseSchema as ContractReservationSecurityDepositResponseSchema,
  SecurityDepositRecordSchema,
  StartOnboardingResponseSchema,
  OnboardingListResponseSchema,
  OnboardingNudgeResponseSchema,
  PropertyProfileResponseSchema,
  AdminPropertyProfileRevisionsResponseSchema,
  PropertyDocumentsListResponseSchema,
  PropertyDocumentUploadResponseSchema,
  LinkUplistingResponseSchema,
} from '@natalan/capehost-contracts'

/**
 * Get API base URL, throwing an error if not configured
 * This allows React to render and show ConfigError UI instead of crashing at module load
 */
function getApiBaseUrl(): string {
  const url = import.meta.env.VITE_API_BASE_URL
  if (!url || (typeof url === 'string' && url.trim() === '')) {
    throw new Error(
      'VITE_API_BASE_URL environment variable is required but not set. ' +
        'Set it to your Cloudflare Worker API URL (e.g., https://your-worker.your-subdomain.workers.dev)'
    )
  }
  return url
}

/**
 * Check if API configuration is valid
 */
export function isApiConfigured(): boolean {
  const url = import.meta.env.VITE_API_BASE_URL
  return !!url && (typeof url === 'string' ? url.trim() !== '' : true)
}

/**
 * Get API configuration status
 */
export function getApiConfigStatus(): { isValid: boolean; missing: string[] } {
  const missing: string[] = []
  const url = import.meta.env.VITE_API_BASE_URL
  if (!url || (typeof url === 'string' && url.trim() === '')) {
    missing.push('VITE_API_BASE_URL')
  }
  return {
    isValid: missing.length === 0,
    missing,
  }
}

/**
 * Show runtime warning if API config is missing (but don't block the app)
 */
function checkApiConfig(): void {
  if (!isApiConfigured()) {
    console.warn(
      '⚠️ Missing API configuration: VITE_API_BASE_URL is not set. ' +
        'Please set it in your Cloudflare Pages settings or .env file.'
    )
  }
}

// Check on module load
checkApiConfig()

/**
 * Custom error class for authorization errors
 */
export class AuthorizationError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'AuthorizationError'
  }
}

/**
 * Thrown when a request to the API origin fails at the network level, with
 * no response at all.
 *
 * admin.capehost.ai and api.capehost.ai are separate Cloudflare Access
 * applications with independent sessions that don't expire in sync. When the
 * API's session expires (while the UI's own session is still valid), Access
 * redirects the cross-origin request to a Google sign-in page. That redirect
 * response carries no CORS headers back to the UI's origin, so the browser
 * blocks it from JS entirely — fetch() throws a bare `TypeError: Failed to
 * fetch` with no status code, indistinguishable from a real network outage.
 * `baseUrl` is included so callers can offer a way to re-authenticate (e.g.
 * open the API origin in a new tab, which forces Access's sign-in flow).
 */
export class ApiUnreachableError extends Error {
  baseUrl: string | undefined

  constructor(message: string, { baseUrl }: { baseUrl?: string } = {}) {
    super(message)
    this.name = 'ApiUnreachableError'
    this.baseUrl = baseUrl
  }
}

/**
 * Thrown when a 2xx admin-api response doesn't match the shape its own
 * published contract schema declares -- a version-skewed deploy, a bug in
 * the handler, or a malformed response. Distinct from ApiRequestError
 * (which means the SERVER itself reported a failure) -- this means the
 * server said success but the body doesn't match what it promised.
 */
export class ResponseValidationError extends Error {
  endpoint: string
  issues: z.ZodIssue[]

  constructor(endpoint: string, issues: z.ZodIssue[]) {
    const summary = issues
      .map(issue => `${issue.path.join('.') || '(root)'}: ${issue.message}`)
      .join('; ')
    super(`Response from ${endpoint} did not match the expected shape: ${summary}`)
    this.name = 'ResponseValidationError'
    this.endpoint = endpoint
    this.issues = issues
  }
}

/**
 * Error thrown by `apiRequest` for a non-2xx JSON API response. Kept as a
 * plain `Error` subtype (rather than a dedicated class) since existing catch
 * sites across the app already narrow the caught value with their own local
 * `as { status?; body? }`-style casts rather than an `instanceof` check.
 */
export type ApiRequestError = Error & { status?: number; body?: unknown }

const UNREACHABLE_MESSAGE =
  "Can't reach the API. This usually means your API sign-in session expired " +
  '— click below to re-authenticate. If that doesn’t help, check your connection.'

/**
 * Run a fetch() against the API origin, converting a network-level failure
 * (no response received at all) into an ApiUnreachableError. See
 * ApiUnreachableError for why this specific failure mode needs its own
 * handling instead of falling through as a generic error.
 */
async function fetchApi(url: string, init: RequestInit, baseUrl: string): Promise<Response> {
  try {
    return await fetch(url, init)
  } catch (err) {
    if (err instanceof TypeError) {
      throw new ApiUnreachableError(UNREACHABLE_MESSAGE, { baseUrl })
    }
    throw err
  }
}

/**
 * Create an authenticated fetch request, validating the parsed JSON body
 * against the given Zod schema before returning it -- see
 * ResponseValidationError for why this is real runtime protection rather
 * than a type-only annotation.
 * Uses Cloudflare Access cookies via credentials: "include"
 */
async function apiRequest<S extends z.ZodTypeAny>(
  endpoint: string,
  schema: S,
  options: RequestInit = {}
): Promise<z.infer<S>> {
  const baseUrl = getApiBaseUrl()
  const url = `${baseUrl}${endpoint}`
  // A multipart body sets its own Content-Type, with the boundary.
  const headers = {
    ...(options.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
    ...options.headers,
  }

  const response = await fetchApi(
    url,
    {
      cache: 'no-store',
      ...options,
      headers,
      credentials: 'include', // Send Cloudflare Access cookies
    },
    baseUrl
  )

  if (!response.ok) {
    const errorText = await response.text()

    // Handle authorization errors specifically
    if (response.status === 401 || response.status === 403) {
      throw new AuthorizationError(
        `Not authorized: ${response.status} ${response.statusText}${errorText ? ` - ${errorText}` : ''}`
      )
    }

    // Generic error for other failures
    const error: ApiRequestError = new Error(
      `API request failed: ${response.status} ${response.statusText}${errorText ? ` - ${errorText}` : ''}`
    )
    error.status = response.status
    // Parsed JSON error body (e.g. { code, error, retryAfterMs }), when the
    // response was actually JSON — lets a caller show a specific,
    // actionable reason instead of just the raw response text. `null` when
    // the body wasn't JSON, so callers must check before using it.
    try {
      error.body = errorText ? JSON.parse(errorText) : null
    } catch {
      error.body = null
    }
    throw error
  }

  const data: unknown = await response.json()
  const result = schema.safeParse(data)
  if (!result.success) {
    throw new ResponseValidationError(endpoint, result.error.issues)
  }
  return result.data
}

/**
 * Local schema for the two-argument object `syncPropertyAirbnbReviews` sends
 * back — no contract schema exists for this endpoint (its handler still
 * lives inline in admin-api's app.js, not one of the migrated route files),
 * so this captures only the fields the property reviews tab actually reads
 * (see src/components/property/reviews-tab.tsx's own `SyncReviewsResult`).
 */
/**
 * Local schemas for the cleaning tracker's configuration endpoints (CAP-372,
 * admin-api routes/admin-cleaning-settings.ts). No published contract exists for
 * these yet — same local-schema pattern as the impersonation and Airbnb-review
 * sync schemas below.
 */
export const CleaningTeamSchema = z.object({
  id: z.string(),
  name: z.string(),
  short_name: z.string(),
  /** The Clearing payee. Deliberately a different string from `name`. */
  accounting_name: z.string(),
  emails: z.array(z.string()),
  phone: z.string().optional(),
  color: z.string(),
  /** False retires a crew from new assignment; it is never deleted, because it
   *  stays the payee on every historical job it was assigned to. */
  active: z.boolean(),
  label_mode: z.enum(['nickname', 'address']),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
})

export type CleaningTeam = z.infer<typeof CleaningTeamSchema>

// --- Local model shadow (CAP-718) ---
// Numbers only: GET /admin/local-shadow/summary never returns an answer's text.

const LocalShadowJudgeSchema = z.object({
  judged: z.number(),
  cloud_mean_distance: z.number().nullable(),
  local_mean_distance: z.number().nullable(),
  local_closer: z.number(),
  cloud_closer: z.number(),
  ties: z.number(),
})

const LocalShadowMetricsSchema = z.object({
  rows: z.number(),
  local_ok: z.number(),
  local_failures: z.record(z.string(), z.number()),
  median_cloud_ms: z.number().nullable(),
  median_local_ms: z.number().nullable(),
  median_queue_minutes: z.number().nullable(),
  agreement: z.number().nullable(),
  compared: z.number(),
  /** Local answers production could not read, counted as disagreement. */
  local_unusable: z.number().default(0),
  judge: LocalShadowJudgeSchema.nullable(),
})

const LocalShadowSiteSchema = LocalShadowMetricsSchema.extend({
  call_site: z.string(),
  code: z.string().nullable(),
  label: z.string(),
  shape: z.enum(['json', 'text']),
  agreement_measure: z.string().nullable(),
  judge_measure: z.string().nullable(),
  cutover_rule: z.string().nullable(),
  weeks: z.array(LocalShadowMetricsSchema.extend({ week_start: z.string() })),
})

const LocalShadowSummarySchema = z.object({
  ok: z.literal(true),
  days: z.number(),
  since: z.string(),
  enabled: z.array(z.string()),
  sites: z.array(LocalShadowSiteSchema),
})

export type LocalShadowSummary = z.infer<typeof LocalShadowSummarySchema>
export type LocalShadowSite = z.infer<typeof LocalShadowSiteSchema>
export type LocalShadowMetrics = z.infer<typeof LocalShadowMetricsSchema>

const CleaningTeamsResponseSchema = z.object({
  teams: z.array(CleaningTeamSchema),
  count: z.number(),
})

const CleaningTeamResponseSchema = z.object({ team: CleaningTeamSchema })

export const CleaningGlobalSettingsSchema = z.object({
  checkin_time: z.string(),
  checkout_time: z.string(),
  updated_at: z.string().optional(),
})

/**
 * GET only. The PUT deliberately has its own schema below: admin-api's
 * `handleUpdateCleaningSettings` returns `{ settings }` alone, with no
 * `switches`, so validating the write against this one rejects every
 * SUCCESSFUL save — the change persists and the UI reports a failure.
 */
const CleaningSettingsResponseSchema = z.object({
  settings: CleaningGlobalSettingsSchema,
  switches: z.object({
    tracker_enabled: z.boolean(),
    // No `sends_enabled`: admin-api removed that switch (CAP-533). An older API
    // that still sends it is fine - zod drops the unknown key.
    /** Always false: the switch is a wrangler var, so the UI shows status,
     *  never a toggle. */
    editable: z.boolean(),
    source: z.string(),
  }),
  /**
   * Who crew mail is actually reaching, per crew (CAP-524).
   *
   * A muted crew is redirected to an internal
   * `andrei+<crew>@capehost.ai` address, so the system runs live while nothing
   * lands in front of a cleaner. That state lives in a KV record and is
   * otherwise completely silent — which is the one weakness of configuring a
   * rollout with data instead of a flag, and why the board announces it.
   *
   * Optional, so the console keeps working against an admin-api that predates
   * it: a missing block reads as "no claim either way" and shows no banner,
   * rather than failing the whole settings request.
   */
  muted_crews: z
    .object({
      active: z.boolean(),
      crews: z.array(
        z.object({
          id: z.string(),
          short_name: z.string(),
          muted: z.boolean(),
        })
      ),
    })
    .optional(),
})

/** What the PUT actually returns: the settings, and nothing else. */
const UpdateCleaningSettingsResponseSchema = z.object({
  settings: CleaningGlobalSettingsSchema,
})

export const PropertyCleaningSettingsSchema = z.object({
  property_id: z.string(),
  enabled: z.boolean(),
  default_team_id: z.string().nullable(),
  /** Team id to whole US dollars. A team absent from this map has no agreed
   *  rate — which is NOT the same as a rate of zero. */
  rates: z.record(z.string(), z.number()),
  updated_at: z.string().optional(),
})

export type PropertyCleaningSettings = z.infer<typeof PropertyCleaningSettingsSchema>

const PropertyCleaningResponseSchema = z.object({
  settings: PropertyCleaningSettingsSchema,
  /** Ride along so the rates matrix and default-team picker render from one request. */
  teams: z.array(CleaningTeamSchema),
})

/**
 * Why a tentative cleaning is not confirmed yet, and when it will be (CAP-794).
 * Computed and stored by admin-api; present only while a job is tentative.
 * `confirms_at` is null when an unreadable check-in or booking time means the
 * job will never confirm on its own.
 */
export const CleaningConfirmationPendingSchema = z.object({
  reason: z.enum([
    'outside_window',
    'free_cancel_window',
    'unknown_checkin',
    'unknown_booking_time',
  ]),
  confirms_at: z.string().nullable(),
})

export type CleaningConfirmationPending = z.infer<typeof CleaningConfirmationPendingSchema>

/**
 * A cleaning job as the jobs endpoints present it (CAP-373/CAP-374): the stored
 * record plus the two facts admin-api derives so the UI never recomputes them
 * (`present()` in admin-api's routes/admin-cleaning-jobs.ts). Deriving
 * `substate` here instead would be the admin console inventing business state,
 * which this workspace's mental model rules out.
 */
export const CleaningJobSchema = z.object({
  id: z.string(),
  property_id: z.string(),
  source: z.object({
    kind: z.enum(['reservation', 'owner_block', 'external_block', 'manual']),
    id: z.string(),
  }),
  status: z.enum(['scheduled', 'completed', 'cancelled', 'not_required']),
  checkout_at: z.string(),
  next_checkin_at: z.string().nullable().optional(),
  /**
   * The DEPARTING stay's check-in - when the guests being cleaned up after
   * arrived. Null for a block and for any job derived before CAP-408. The
   * board's Stay column reads it: a fortnight's stay is a different job from
   * two nights, which is how the crews read a calendar.
   */
  stay_checkin_at: z.string().nullable().optional(),
  /** When the cleaning happens. Deliberately separate from `checkout_at`. */
  scheduled_at: z.string(),
  scheduled_at_explicit: z.boolean().optional(),
  /** Unset means tentative: the crew sees it, dimmed, but nothing is committed. */
  confirmed_at: z.string().nullable().optional(),
  confirmation_pending: CleaningConfirmationPendingSchema.nullable().optional(),
  needs_decision: z.boolean().optional(),
  team_id: z.string().nullable().optional(),
  assigned_by: z.enum(['auto', 'manual']).optional(),
  assigned_at: z.string().nullable().optional(),
  acked_at: z.string().nullable().optional(),
  acked_by: z.string().nullable().optional(),
  not_required_reason: z.enum(['source', 'operator']).optional(),
  note: z.string().nullable().optional(),
  pinned_at: z.string().nullable().optional(),
  payable: z
    .object({
      sent_at: z.string(),
      amount: z.number(),
      payee: z.string(),
      message_id: z.string().optional(),
    })
    .nullable()
    .optional(),
  notices_sent: z
    .array(
      z.object({
        kind: z.enum(['confirmed', 'rescheduled', 'stay_changed', 'cancelled']),
        describes: z.string().optional(),
        at: z.string(),
      })
    )
    .optional(),
  heads_up: z
    .object({
      kind: z.enum(['confirmed', 'rescheduled', 'stay_changed', 'cancelled']),
      detail: z.string(),
      raised_at: z.string(),
      emailed_at: z.string().optional(),
      told_at: z.string().optional(),
    })
    .nullable()
    .optional(),
  created_at: z.string(),
  updated_at: z.string(),
  /** Derived server-side; null once the job is no longer `scheduled`. */
  substate: z.enum(['needs_decision', 'tentative', 'needs_team', 'needs_ack', 'ready']).nullable(),
  completion_eligible: z.boolean(),
  /**
   * The PROPERTY's own calendar day for `scheduled_at` - the day the crew
   * works - computed by admin-api alongside `substate` and for the same
   * reason. Deriving it here from `scheduled_at` with the console's own guess
   * at the zone would be a second implementation of a rule that already has
   * one, and a 20:30 Eastern cleaning has already rolled over in UTC.
   * Optional so a cached response from before CAP-412 still parses.
   */
  local_date: z.string().nullable().optional(),
  time_zone: z.string().optional(),
  /**
   * What the last operator action was, while it can still be taken back
   * (CAP-511), or null when there is nothing to undo.
   *
   * The snapshot it would restore stays server-side - this is only whether the
   * Undo button is live. Optional so a response cached from before CAP-511
   * still parses.
   */
  undoable: z.object({ action: z.string(), at: z.string() }).nullable().optional(),
})

export type CleaningJob = z.infer<typeof CleaningJobSchema>

/**
 * The paging and facet block both cleaning list endpoints return (CAP-412).
 *
 * `total` is what the filters matched and `returned` is what came back on this
 * page - a pager built on the array's length cannot tell a last page from an
 * only page. `total_unfiltered` is what the tab badge reads, so narrowing the
 * view can never make the amount of outstanding work look smaller than it is.
 *
 * Facets are counted server-side over the UNFILTERED set, which is why they
 * are not derived here: a count computed from the rows on screen would say how
 * much you can already see, which is the one number nobody needs.
 */
const CleaningPagingSchema = {
  count: z.number(),
  total: z.number(),
  total_unfiltered: z.number(),
  returned: z.number(),
  limit: z.number(),
  offset: z.number(),
}

const CleaningWindowFacetsSchema = z.object({
  overdue: z.number(),
  today: z.number(),
  upcoming: z.number(),
})

const CleaningJobsResponseSchema = z.object({
  jobs: z.array(CleaningJobSchema),
  ...CleaningPagingSchema,
  today: z.string().nullable(),
  facets: z.object({
    status: z.record(z.string(), z.number()),
    substate: z.record(z.string(), z.number()),
    property: z.record(z.string(), z.number()),
    team: z.record(z.string(), z.number()),
    window: CleaningWindowFacetsSchema,
  }),
  /** True when the scan was clipped, so the list is incomplete and says so. */
  truncated: z.boolean(),
})

/**
 * What is behind a job, for the screen that opens one (CAP-418).
 *
 * Null for a block, whose dates the job already carries, and for a manual job,
 * which has no source. Deliberately carries no contact details: a cleaning
 * drawer asks who was here and for how long, and admin-api does not return
 * them.
 */
export const CleaningJobSourceSchema = z.object({
  kind: z.enum(['reservation', 'owner_block', 'external_block', 'manual']),
  guest_name: z.string().nullable(),
  check_in: z.string().nullable(),
  check_out: z.string().nullable(),
  nights: z.number().nullable(),
  channel: z.string().nullable(),
  status: z.string().nullable(),
})

export type CleaningJobSource = z.infer<typeof CleaningJobSourceSchema>

const CleaningJobDetailResponseSchema = z.object({
  job: CleaningJobSchema,
  source: CleaningJobSourceSchema.nullable().optional(),
})

/**
 * A job's timeline, newest first (CAP-384).
 *
 * `source` says which of two things this is. `events` is the real stored log:
 * every write, with the time, who made it and the fields it moved. `derived` is
 * the fallback for a job last written before that log existed - reconstructed
 * from the job's own timestamps, so it can say when but usually not who, and
 * marks those actors `unknown` rather than guessing.
 */
const CleaningJobHistoryResponseSchema = z.object({
  job_id: z.string(),
  property_id: z.string(),
  source: z.enum(['events', 'derived']).optional(),
  entries: z.array(
    z.object({
      at: z.string(),
      event: z.string(),
      detail: z.string().optional(),
      actor: z
        .object({
          kind: z.enum(['person', 'process', 'crew', 'unknown']),
          id: z.string(),
          /** Coarse, IP-derived. Never proof of presence. */
          location: z.string().optional(),
        })
        .optional(),
      /** Each field this write moved, so the UI can spell the values itself. */
      changed: z
        .array(
          z.object({
            field: z.string(),
            from: z.string().nullable(),
            to: z.string().nullable(),
          })
        )
        .optional(),
      /**
       * What a relayed ack was actually backed by (CAP-467).
       *
       * Present only on an ack the crew gave by tapback-liking a text, which
       * someone then recorded on their behalf. `reacted_at` is the crew's own
       * moment, `message_sha256` pins which message they reacted to, and
       * `proof_id` addresses a screenshot the API serves behind Access.
       */
      evidence: z
        .object({
          source: z.literal('imessage_tapback'),
          reacted_at: z.string(),
          message_sha256: z.string(),
          message_bytes: z.number(),
          chat_guid: z.string().nullish(),
          proof_id: z.string().nullish(),
          /** The crew ask this ack answered (CAP-709). */
          ask_id: z.string().nullish(),
        })
        .optional(),
      /** The crew ask this entry belongs to: "asked", and an ack that answered one (CAP-709). */
      ask_id: z.string().optional(),
    })
  ),
  note: z.string().optional(),
})

export type CleaningJobHistory = z.infer<typeof CleaningJobHistoryResponseSchema>

const CleaningJobResponseSchema = z.object({
  job: CleaningJobSchema,
  /** Present when the action produced something the crew must be told about. */
  change: z
    .object({
      kind: z.enum(['confirmed', 'rescheduled', 'stay_changed', 'cancelled']),
      detail: z.string(),
    })
    .optional(),
})

export const CLEANING_ACTION_KINDS = [
  'pinned',
  'block_needs_decision',
  'assign_team',
  'paying_today_never_acked',
  'payable_problem',
  'crew_heads_up',
  'awaiting_ack',
  'completed_but_cancelled',
  // `same_day_turnover` was removed in CAP-421: "Needs action" is work to do,
  // and a turnover carries none. It survives as the `same_day` BADGE on
  // whatever row a job does raise.
] as const

export type CleaningActionKind = (typeof CLEANING_ACTION_KINDS)[number]

/**
 * Where a job is in its life: the working substate while it is scheduled,
 * otherwise its stored status. Derived by admin-api from the job and never
 * stored, so it cannot drift from the facts it summarises (CAP-531).
 */
export const CLEANING_ITEM_STATUSES = [
  'needs_decision',
  'tentative',
  'needs_team',
  'needs_ack',
  'ready',
  'completed',
  'cancelled',
  'not_required',
] as const

export type CleaningItemStatus = (typeof CLEANING_ITEM_STATUSES)[number]

/** One reason a job is on the list. */
export const CleaningActionReasonSchema = z.object({
  kind: z.enum(CLEANING_ACTION_KINDS),
  detail: z.string(),
  /** Kept by the API for analytics; the board does not show it. */
  days_waiting: z.number().nullable(),
})

export type CleaningActionReason = z.infer<typeof CleaningActionReasonSchema>

/**
 * One job on the board, with every reason it is there (CAP-531).
 *
 * The list used to be one row per REASON, which put a job with two reasons on
 * screen twice - correct both times, and never naming the other. An item is
 * the job the way it is thought about: one thing, in one state, with tags.
 */
export const CleaningActionItemSchema = z.object({
  property_id: z.string(),
  property_name: z.string().nullable(),
  job_id: z.string(),
  scheduled_at: z.string(),
  local_date: z.string().nullable(),
  team_id: z.string().nullable(),
  status: z.enum(CLEANING_ITEM_STATUSES),
  /** Most urgent first, and never empty. */
  reasons: z.array(CleaningActionReasonSchema).min(1),
  note: z.string().nullable(),
  badges: z.array(z.string()),
  confirmation_pending: CleaningConfirmationPendingSchema.nullable().optional(),
  days_waiting: z.number().nullable(),
})

export type CleaningActionItem = z.infer<typeof CleaningActionItemSchema>

/**
 * One item per job, ranked by its most urgent reason. Every count is jobs,
 * except the kind facet, which counts a job once under each of its reasons.
 */
const CleaningActionItemsResponseSchema = z.object({
  generated_at: z.string(),
  today: z.string().nullable(),
  ...CleaningPagingSchema,
  items: z.array(CleaningActionItemSchema),
  facets: z.object({
    kind: z.record(z.string(), z.number()),
    property: z.record(z.string(), z.number()),
    team: z.record(z.string(), z.number()),
    window: CleaningWindowFacetsSchema,
  }),
  /**
   * Local date to team id to how many cleanings that crew has that day.
   * Computed from every job, never the page: a crew's load is a fact about the
   * day, and recomputing it from 50 sliced rows would report a crew as free on
   * a day they are booked solid.
   */
  crew_load: z.record(z.string(), z.record(z.string(), z.number())),
  /**
   * Enabled properties that cannot yet run their own cleanings (CAP-381).
   *
   * Computed from the settings rather than the jobs, and unaffected by the
   * row filters, because a property nobody configured has no jobs to filter
   * on until its first booking - which is exactly too late to find out that
   * no crew was picked or no rate agreed.
   *
   * Optional so a UI deployed ahead of the API does not fail its parse.
   */
  unconfigured_properties: z
    .array(
      z.object({
        property_id: z.string(),
        property_name: z.string().nullable(),
        missing: z.array(z.enum(['crew', 'rate'])),
        default_team_id: z.string().nullable(),
      })
    )
    .optional(),
  truncated: z.boolean(),
})

/**
 * The batched text Andrei sends a crew, and the outstanding jobs it covers -
 * every confirmed job that crew has not yet acked, grouped and ordered the
 * same way the text itself is (CAP-438). `message` is null when the crew has
 * nothing outstanding, so a caller never sends an empty greeting.
 */
export const CleaningCrewMessageItemSchema = z.object({
  property_id: z.string(),
  job_id: z.string(),
  property: z.string(),
  scheduled_at: z.string(),
  checkout_at: z.string(),
})

export const CleaningCrewMessageResponseSchema = z.object({
  team: z.object({ id: z.string(), name: z.string() }),
  message: z.string().nullable(),
  items: z.array(CleaningCrewMessageItemSchema),
  count: z.number(),
  /** A clipped scan may be missing a house - worth knowing before this is sent. */
  truncated: z.boolean(),
})

export type CleaningCrewMessageResponse = z.infer<typeof CleaningCrewMessageResponseSchema>

/**
 * A crew ask (CAP-709): one crew message recorded as sent, with every job it
 * named at the time it was asked, and the crew's answer once recorded.
 */
export const CLEANING_ASK_STATUSES = ['waiting', 'acked', 'no_reply', 'superseded'] as const
export type CleaningAskStatus = (typeof CLEANING_ASK_STATUSES)[number]

export const CleaningAskSchema = z.object({
  id: z.string(),
  team_id: z.string(),
  team_name: z.string(),
  message: z.string(),
  message_sha256: z.string(),
  message_bytes: z.number(),
  items: z.array(
    z.object({
      property_id: z.string(),
      job_id: z.string(),
      property: z.string(),
      scheduled_at: z.string(),
    })
  ),
  status: z.enum(CLEANING_ASK_STATUSES),
  sent_at: z.string(),
  sent_by: z.string(),
  reacted_at: z.string().nullish(),
  proof_id: z.string().nullish(),
  answered_at: z.string().nullish(),
  answered_by: z.string().nullish(),
  closed_at: z.string().nullish(),
  closed_by: z.string().nullish(),
  superseded_by: z.string().nullish(),
})
export type CleaningAsk = z.infer<typeof CleaningAskSchema>

const CleaningAskResponseSchema = z.object({ ask: CleaningAskSchema })
const CleaningAsksResponseSchema = z.object({
  asks: z.array(CleaningAskSchema),
  truncated: z.boolean(),
})
const CleaningAskAnswerResponseSchema = z.object({
  ask: CleaningAskSchema,
  acked: z.number(),
  already_acked: z.number(),
})
const CleaningAckProofUploadSchema = z.object({
  proof_id: z.string(),
  content_type: z.string(),
  bytes: z.number(),
  deduped: z.boolean(),
})

/** Why one job in an ask could not take the crew's answer (a 409's `problems`). */
export interface CleaningAskProblem {
  property_id: string
  job_id: string
  property: string
  reason: string
}

/** The value both endpoints accept for "the ones with nothing set". */
export const CLEANING_UNSET_FILTER = 'none'

/** Filters shared by both list endpoints, spelled identically on purpose. */
export interface CleaningListFilters {
  propertyId?: string
  /** A crew id, or `none` for the unassigned. */
  teamId?: string
  /** A bare `YYYY-MM-DD` is the property's own day, inclusive at both ends. */
  from?: string
  to?: string
  limit?: number
  offset?: number
}

/** Writes the shared filters onto a query string, omitting the defaults. */
function applyCleaningFilters(params: URLSearchParams, filters: CleaningListFilters): void {
  if (filters.propertyId) params.set('propertyId', filters.propertyId)
  if (filters.teamId) params.set('teamId', filters.teamId)
  if (filters.from) params.set('from', filters.from)
  if (filters.to) params.set('to', filters.to)
  if (filters.limit != null) params.set('limit', String(filters.limit))
  if (filters.offset) params.set('offset', String(filters.offset))
}

/**
 * Local schema for POST /admin/portal/impersonation and its revoke sibling — no published
 * contract exists yet for either (routes/portal-impersonation.ts in admin-api), same
 * pattern as SyncPropertyAirbnbReviewsResponseSchema below.
 */
const StartPortalImpersonationResponseSchema = z.object({
  impersonationId: z.string(),
  token: z.string(),
  portalUrl: z.string(),
  expiresAt: z.string(),
  ttlSeconds: z.number(),
  targetUser: z.object({ id: z.string(), email: z.string() }),
})

const RevokePortalImpersonationResponseSchema = z.object({
  revoked: z.boolean(),
})

const SyncPropertyAirbnbReviewsResponseSchema = z.object({
  skipped: z.boolean().optional(),
  error: z.string().nullable().optional(),
  reviewsFound: z.number().optional(),
  reviewsStored: z.number().optional(),
  reviewsMatched: z.number().optional(),
})

/**
 * Local schema for GET /admin/reservations/:id/review-confidence (CAP-301)
 * — no published contract exists yet for this endpoint (see admin-api's
 * LIMITATIONS.md entry for it), same pattern as
 * StartPortalImpersonationResponseSchema above.
 */
const ReviewConfidenceSignalSchema = z.object({
  type: z.string(),
  detail: z.string(),
})

const ReviewConfidenceIssueSchema = z.object({
  issue: z.string(),
  // true = confirmed fixed in the thread, "mitigated" = worked around and
  // still wrong when the guest left (CAP-398), false = left open, null = the
  // thread never said either way.
  //
  // The catch is load-bearing, not defensive decoration: this schema parses a
  // live API response, so a state added on the admin-api side before this
  // repo knows about it would otherwise fail validation and take the whole
  // confidence panel down. An unrecognized state degrades to "the thread
  // never said", which is what an unknown state honestly means here.
  resolved: z
    .union([z.boolean(), z.literal('mitigated')])
    .nullable()
    .catch(null),
})

const ReviewConfidenceFactsSchema = z.object({
  checkout_tone: z.string().nullable(),
  explicit_rating_promise: z.boolean(),
  issues: z.array(ReviewConfidenceIssueSchema),
})

const ReviewConfidenceSchema = z.object({
  status: z.enum(['queued', 'completed', 'failed']),
  version: z.number(),
  score: z.number().nullable(),
  signals: z.array(ReviewConfidenceSignalSchema),
  summary: z.string(),
  // Structured facts extracted alongside the score; absent on records
  // written before the field existed.
  facts: ReviewConfidenceFactsSchema.nullable().optional(),
  error: z.string().nullable().optional(),
})

const ChaseRecommendationSchema = z.object({
  tier: z.enum(['green', 'amber', 'red']),
  review: z.enum(['standard', 'skip']),
  thank_you: z.enum(['standard', 'soft', 'none']),
  reminder_d7: z.enum(['optional', 'one_soft', 'never']),
  reminder_d13: z.literal('never'),
  why: z.array(z.string()),
})

const PropertyPriorSchema = z.object({
  name: z.string(),
  stays: z.number(),
  five_star: z.number(),
  sub_five: z.number(),
  no_review: z.number(),
  five_rate: z.number(),
  sub_rate: z.number(),
})

const GetReviewConfidenceResponseSchema = z.object({
  confidence: ReviewConfidenceSchema.nullable(),
  recommendation: ChaseRecommendationSchema.nullable().optional(),
  property_prior: PropertyPriorSchema.nullable().optional(),
})

const OutcomeCountsSchema = z.object({
  n: z.number(),
  five_star: z.number(),
  sub_five: z.number(),
  no_review: z.number(),
  pending: z.number(),
})

const ReviewConfidenceCalibrationSchema = z.object({
  computed_at: z.string(),
  records_scored: z.number(),
  resolved: z.number(),
  tiers: z.object({
    green: OutcomeCountsSchema,
    amber: OutcomeCountsSchema,
    red: OutcomeCountsSchema,
  }),
  auc_five_vs_not: z.number().nullable(),
  reminders: z.object({
    clicks: z.number(),
    converted_within_4_days: z.number(),
    stays_reminded: z.number(),
    stays_skipped: z.number(),
  }),
  guest_reviewed_first: OutcomeCountsSchema,
  guest_reviewed_first_timing: z
    .object({
      n: z.number(),
      median_hours_after_checkout: z.number().nullable(),
      within_24h: z.number(),
    })
    .optional(),
  misses: z.array(
    z.object({
      reservation_id: z.string(),
      property_id: z.string().nullable(),
      score: z.number(),
      rating: z.number(),
      signal_count: z.number(),
      checkout_tone: z.string().nullable(),
      computed_at: z.string().nullable(),
    })
  ),
  properties: z.record(z.string(), PropertyPriorSchema),
  // Outcome by signal type ("any" / "none" included); absent on a summary
  // written before the nightly job computed it.
  signals: z.record(z.string(), OutcomeCountsSchema).optional(),
  // Cumulative share of reviews by days after checkout, index = day.
  timing: z
    .object({
      reviewed: z.number(),
      five_star: z.number(),
      sub_five: z.number(),
      five_star_cum: z.array(z.number()),
      sub_five_cum: z.array(z.number()),
      five_share_d0_1: z.number().nullable(),
      five_share_d8_plus: z.number().nullable(),
    })
    .optional(),
})

const GetReviewConfidenceCalibrationResponseSchema = z.object({
  calibration: ReviewConfidenceCalibrationSchema.nullable(),
})

// --- Reviews page: nightly statistics and the attention to-do (admin-api
// services/review-stats.ts and services/review-attention.ts). Not in
// @natalan/capehost-contracts yet.

const StarHistogramSchema = z.object({
  5: z.number(),
  4: z.number(),
  3: z.number(),
  2: z.number(),
  1: z.number(),
})

const RatingWindowSchema = z.object({
  reviews: z.number(),
  avg: z.number().nullable(),
  five_star: z.number(),
  sub_five: z.number(),
  stays: z.number(),
  reviewed: z.number(),
  review_rate: z.number().nullable(),
})

const SubratingsSchema = z.object({
  accuracy: z.number().optional(),
  value: z.number().optional(),
  cleanliness: z.number().optional(),
  location: z.number().optional(),
  check_in: z.number().optional(),
  communication: z.number().optional(),
})

// How the reviews that reported each sub-rating actually scored it, so
// "value averages 4.83" can be read as the count of guests who marked it
// down. Optional while the nightly stats doc predating it is still current.
const SubratingStarsSchema = z.object({
  accuracy: StarHistogramSchema.optional(),
  value: StarHistogramSchema.optional(),
  cleanliness: StarHistogramSchema.optional(),
  location: StarHistogramSchema.optional(),
  check_in: StarHistogramSchema.optional(),
  communication: StarHistogramSchema.optional(),
})

const PropertyReviewStatsSchema = z.object({
  property_id: z.string(),
  name: z.string(),
  reviews: z.number(),
  avg: z.number().nullable(),
  stars: StarHistogramSchema,
  three_and_below: z.number(),
  subratings: SubratingsSchema,
  weakest_subrating: z.string().nullable(),
  stays: z.number(),
  no_review: z.number(),
  review_rate: z.number().nullable(),
  pending_no_review: z.number(),
  this_year: RatingWindowSchema,
  last_year: RatingWindowSchema,
})

const ReviewStatsSchema = z.object({
  computed_at: z.string(),
  portfolio: z.object({
    reviews: z.number(),
    avg: z.number().nullable(),
    five_star: z.number(),
    sub_five: z.number(),
    stays: z.number(),
    no_review: z.number(),
    pending_no_review: z.number(),
    review_rate: z.number().nullable(),
    stars: StarHistogramSchema,
    subratings: SubratingsSchema,
    subrating_stars: SubratingStarsSchema.optional(),
  }),
  monthly: z.array(
    z.object({
      month: z.string(),
      reviews: z.number(),
      avg: z.number().nullable(),
      sub_five: z.number(),
    })
  ),
  year_over_year: z.object({
    as_of: z.string(),
    this_year: RatingWindowSchema,
    last_year: RatingWindowSchema,
  }),
  properties: z.array(PropertyReviewStatsSchema),
})

const GetReviewStatsResponseSchema = z.object({ stats: ReviewStatsSchema.nullable() })

const AttentionStatusSchema = z.enum(['open', 'done', 'skipped'])

const AttentionItemSchema = z.object({
  id: z.string(),
  property_id: z.string(),
  property_name: z.string(),
  title: z.string(),
  category: z.string(),
  status: AttentionStatusSchema,
  evidence: z.array(
    z.object({
      review_id: z.string(),
      reservation_id: z.string().nullable(),
      rating: z.number(),
      date: z.string().nullable(),
      quote: z.string(),
      source: z.enum(['review', 'private_note']),
    })
  ),
  created_at: z.string(),
  updated_at: z.string(),
  last_seen: z.string().nullable(),
  resolved_at: z.string().nullable(),
  reopened: z.number(),
})

const GetReviewAttentionResponseSchema = z.object({
  updated_at: z.string(),
  items: z.array(AttentionItemSchema),
  reviews_read: z.number(),
})

const SetReviewAttentionStatusResponseSchema = z.object({ item: AttentionItemSchema })

export type ReviewConfidenceSignal = z.infer<typeof ReviewConfidenceSignalSchema>
export type ReviewConfidence = z.infer<typeof ReviewConfidenceSchema>
export type ReviewConfidenceIssue = z.infer<typeof ReviewConfidenceIssueSchema>
export type ChaseRecommendation = z.infer<typeof ChaseRecommendationSchema>
export type PropertyPrior = z.infer<typeof PropertyPriorSchema>
export type ReviewConfidenceCalibration = z.infer<typeof ReviewConfidenceCalibrationSchema>
export type ReviewStats = z.infer<typeof ReviewStatsSchema>
export type RemindPreflight = z.infer<typeof RemindPreflightSchema>
export type PropertyReviewStats = z.infer<typeof PropertyReviewStatsSchema>
export type AttentionItem = z.infer<typeof AttentionItemSchema>
export type AttentionStatus = z.infer<typeof AttentionStatusSchema>

/**
 * The digest's embedded per-row suggestion (see review-chase.tsx's
 * `ReviewSuggestionEmbed`/`DigestRow` comments and
 * suggested-review-block.tsx's `ReviewSuggestion`) — proactively generated
 * by the review-window-open webhook and genuinely present on the wire, but
 * not modeled by ReviewChase*RowSchema in @natalan/capehost-contracts. A
 * real contract gap: without this extension, .safeParse() silently strips
 * `reviewSuggestion` from every row (Zod objects strip unrecognized keys by
 * default), which previously worked by accident only because the old
 * type-only cast never actually validated anything at runtime.
 */
/**
 * Orphan-gap offer candidates for one property.
 *
 * Defined locally rather than imported from @natalan/capehost-contracts: this
 * endpoint shipped in admin-api after the contracts package's last release, so
 * there is nothing upstream to import yet. It belongs in that package once it
 * is cut — same situation the ReviewChase suggestion embed above documents,
 * arrived at from the other direction.
 */
const OrphanGapNightPriceSchema = z.object({
  date: z.string(),
  marketNightly: z.number().nullable(),
  suggestedNightly: z.number(),
  cappedBy: z.string(),
})

const OrphanGapPricingSchema = z.object({
  paidNightly: z.number(),
  discountRate: z.number(),
  nights: z.array(OrphanGapNightPriceSchema),
  offerValue: z.number(),
  uniform: z.boolean(),
})

const OrphanGapStageSchema = z.object({
  stage: z.string(),
  reservationId: z.string().nullable(),
  eligible: z.boolean(),
  reason: z.string().nullable(),
  reasonLabel: z.string().nullable(),
  guestName: z.string().nullable(),
  channel: z.string().nullable(),
  pricing: OrphanGapPricingSchema.nullable(),
  message: z.string().nullable(),
})

const OrphanGapRowSchema = z.object({
  propertyId: z.string(),
  propertyName: z.string().nullable(),
  gapKey: z.string(),
  firstNight: z.string(),
  lastNight: z.string(),
  gapNights: z.number(),
  minStay: z.number(),
  nights: z.array(z.string()),
  stages: z.array(OrphanGapStageSchema),
  actionable: z.boolean(),
  offerValue: z.number(),
})

/**
 * Why a property's answer is incomplete. Every flag means "we could not find
 * out", never "there is nothing" — the page has to be able to tell an operator
 * which of the two an empty table represents.
 */
const OrphanGapTruncationSchema = z.object({
  propertyId: z.string(),
  reservations: z.boolean(),
  reservationsStale: z.boolean(),
  reservationCoverage: z.boolean(),
  sendableChecks: z.boolean(),
  ownerBlocks: z.boolean(),
  calendar: z.boolean(),
})

export const OrphanGapOffersResponseSchema = z.object({
  ok: z.boolean(),
  as_of: z.string(),
  rows: z.array(OrphanGapRowSchema),
  errors: z.array(
    z.object({ propertyId: z.string(), error: z.string(), code: z.string().optional() })
  ),
  truncated: z.array(OrphanGapTruncationSchema),
  counts: z.object({ properties: z.number(), gaps: z.number(), actionable: z.number() }),
})

export type OrphanGapOffersResponse = z.infer<typeof OrphanGapOffersResponseSchema>
export type OrphanGapRow = z.infer<typeof OrphanGapRowSchema>
export type OrphanGapStage = z.infer<typeof OrphanGapStageSchema>
export type OrphanGapTruncation = z.infer<typeof OrphanGapTruncationSchema>

/**
 * One property's stored answer from the last scan that reached it.
 *
 * `computedAt` is null when a property has never been scanned, which is also
 * the only case where `pending` and an empty `rows` mean the same thing —
 * everywhere else "not scanned yet" and "scanned, nothing found" are different
 * claims and the page has to keep them apart.
 */
const OrphanGapSnapshotPropertySchema = z.object({
  propertyId: z.string(),
  propertyName: z.string().nullable(),
  computedAt: z.string().nullable(),
  asOf: z.string().nullable(),
  rows: z.array(OrphanGapRowSchema),
  truncation: OrphanGapTruncationSchema.nullable(),
  error: z.string().nullable(),
  pending: z.boolean(),
  /**
   * This run answered with a failure the queue is still retrying.
   *
   * Distinct from `pending`: the answer on screen IS this run's, so its rows
   * must not be labelled as carried over - but the run is not finished, so the
   * page must not stop polling either.
   */
  retrying: z.boolean(),
})

export const OrphanGapSnapshotResponseSchema = z.object({
  ok: z.boolean(),
  run: z
    .object({
      run_id: z.string(),
      trigger: z.string(),
      as_of: z.string(),
      started_at: z.string(),
      property_ids: z.array(z.string()),
    })
    .nullable(),
  /** The OLDEST property answer, which is the age the whole page can honestly claim. */
  computedAt: z.string().nullable(),
  running: z.boolean(),
  /** Properties the last run finished without answering for. Zero while it is still working. */
  unanswered: z.number(),
  properties: z.array(OrphanGapSnapshotPropertySchema),
  rows: z.array(OrphanGapRowSchema),
  counts: z.object({
    properties: z.number(),
    gaps: z.number(),
    actionable: z.number(),
    pending: z.number(),
  }),
})

const OrphanGapScanResponseSchema = z.object({
  ok: z.boolean(),
  run: z
    .object({
      run_id: z.string(),
      /** What the caller compares against the snapshot's run to know it has landed. */
      started_at: z.string(),
      property_ids: z.array(z.string()),
    })
    .nullable()
    .optional(),
})

export type OrphanGapSnapshotResponse = z.infer<typeof OrphanGapSnapshotResponseSchema>
export type OrphanGapSnapshotProperty = z.infer<typeof OrphanGapSnapshotPropertySchema>

const ReviewSuggestionEmbedSchema = z.object({
  status: z.enum(['queued', 'completed', 'failed']),
  version: z.number(),
  // suggested-review-block.tsx's ReviewSuggestion.review_text is
  // `string | undefined` (no null) -- the wire response can send null for a
  // not-yet-generated suggestion, so normalize null to undefined here rather
  // than widening that component's own type just for this embed.
  review_text: z
    .string()
    .nullable()
    .optional()
    .transform(value => value ?? undefined),
  error: z.string().nullable().optional(),
})

// hostReviewedAt / reminderSentCount (CAP-301): the host's own review of the
// guest as recorded on the chase, and reminders actually sent from the
// portal. Not in the contracts package yet, same gap as reviewSuggestion.
const ReviewChaseDigestResponseWithSuggestionsSchema = ReviewChaseDigestResponseSchema.extend({
  checkoutTodayRows: z.array(
    ReviewChaseCheckoutTodayRowSchema.extend({
      reviewSuggestion: ReviewSuggestionEmbedSchema.nullable().optional(),
      hostReviewedAt: z.string().nullable().optional(),
      // When the checkout-day thank-you went out (CAP-393), so the Today
      // card stops offering the button on a guest already thanked.
      thankYouSentAt: z.string().nullable().optional(),
      // The chase's id when one exists on checkout day; what "I left a
      // review" and Remind need to address the chase.
      externalId: z.string().nullable().optional(),
    })
  ),
  pendingRows: z.array(
    ReviewChasePendingRowSchema.extend({
      reviewSuggestion: ReviewSuggestionEmbedSchema.nullable().optional(),
      hostReviewedAt: z.string().nullable().optional(),
      reminderSentCount: z.number().optional(),
      // Which list a row belongs in and its single next action, decided by
      // admin-api's chaseBucket so this page and the nightly digest email
      // never disagree about what needs the operator today. Optional while
      // the deploy rolls out; a row without one is treated as work to do
      // rather than silently hidden.
      bucket: z.enum(['do_today', 'waiting']).optional(),
      action: z.enum(['review', 'remind']).nullable().optional(),
      // What the stay's own confidence recommendation says about a D+7
      // reminder - and so why a waiting row is waiting (CAP-443). Null or
      // absent means the stay was never scored, which admin-api treats as no
      // opinion; the page falls back to the day-count wording.
      //
      // .catch(null) so a value this build does not know - notably the
      // retired 'holdout' arm still arriving from an admin-api mid-deploy
      // (CAP-482) - degrades to "no opinion" instead of failing the parse and
      // blanking the whole digest. No opinion proposes the reminder, which is
      // the right way to be wrong for the few minutes a rollout takes.
      reminderD7: z.enum(['optional', 'one_soft', 'never']).nullable().optional().catch(null),
    })
  ),
})

const RemindThreadMessageSchema = z.object({
  id: z.string().nullable(),
  direction: z.enum(['guest_to_host', 'host_to_guest']),
  at: z.string().nullable(),
  body: z.string(),
})

const RemindPreflightSchema = z.object({
  chase: z.object({
    platform: z.string(),
    external_id: z.string(),
    guest_name: z.string(),
    checkout_date: z.string(),
    status: z.string(),
    guest_reviewed_first: z.boolean(),
    host_reviewed_at: z.string().nullable(),
    reminder_sends: z.array(z.string()),
    reminder_clicks: z.array(z.string()),
  }),
  reservation_id: z.string().nullable(),
  thread_id: z.string().nullable(),
  messages: z.array(RemindThreadMessageSchema),
  prior_reminder: z.object({ at: z.string().nullable(), excerpt: z.string() }).nullable(),
  blocked: z.array(z.string()),
  warnings: z.array(z.string()),
  draft: z.string(),
  wording: z.enum(['standard', 'soft']),
})

const RemindSendResponseSchema = z.object({
  sent: z.literal(true),
  replayed: z.boolean(),
  auditId: z.string().nullable(),
  reminder_sends: z.array(z.string()),
})

const HostReviewedResponseSchema = z.object({
  ok: z.literal(true),
  platform: z.string(),
  external_id: z.string(),
  host_reviewed_at: z.string().nullable(),
  host_review_source: z.string().nullable(),
})

/**
 * Another real contract gap: property/reservation-summary records carry a
 * legacy `external.uplisting` blob (format-utils.ts's `getPropertyPhotoUrl`
 * reads `external.uplisting.relationships.photos`, reservation-sidebar.tsx
 * reads `external.uplisting.lock_code`), but GetPropertyResponseSchema and
 * ReservationSummarySchema don't model an `external` field at all -- unlike
 * ReservationSchema elsewhere in the same package, which already has
 * `external: z.unknown().optional()`. Left as z.unknown() here too (rather
 * than modeling the whole uplisting blob) since nothing in this file reads
 * into it; only the consuming components do their own defensive optional
 * chaining.
 */
const GetPropertyResponseWithExternalSchema = GetPropertyResponseSchema.extend({
  external: z.unknown().optional(),
  /**
   * Real contract gap (CAP-171 / admin-api PR #405, in review at the time
   * this was added): the same property KV record backs both this
   * single-property response and the list response below, and PR #405 adds
   * `archived`/`managed_until` to that record for property offboarding.
   * @natalan/capehost-contracts doesn't model either field on either
   * response shape yet. Optional here (rather than required) so this stays
   * safe even if the single-property handler turns out not to surface them.
   */
  archived: z.boolean().optional(),
  managed_until: z.string().nullable().optional(),
})
const PropertyExternalBlockSchema = z.object({
  id: z.string(),
  property_id: z.string(),
  /** Inclusive first blocked day, `YYYY-MM-DD`. */
  start: z.string(),
  /** EXCLUSIVE last day, `YYYY-MM-DD` - the first day free again. */
  end: z.string(),
  source: z.string().optional(),
  first_seen_at: z.string().optional(),
  last_seen_at: z.string().optional(),
})

export type PropertyExternalBlock = z.infer<typeof PropertyExternalBlockSchema>

/**
 * `GET /admin/properties/:id/calendar` (admin-api CAP-872): every dated thing
 * about one property across a day range, one layer each. No published contract
 * models it yet, hence the local schema - same pattern as the external-blocks
 * read above. Only what the calendar reads is declared.
 *
 * A layer is its items, or the reason it has none: an unreadable source never
 * passes for an empty one, so the UI shows an error chip rather than a quiet
 * gap. Layers the UI does not draw yet (the ones still to come) are left
 * unmodelled and arrive as `unknown`, so a layer with a new shape can never
 * fail the whole calendar.
 */
const calendarLayer = <Item extends z.ZodTypeAny>(item: Item) =>
  z.discriminatedUnion('status', [
    z.object({ status: z.literal('ok'), items: z.array(item) }),
    z.object({ status: z.literal('error'), message: z.string() }),
  ])

const PropertyCalendarStaySchema = z.object({
  id: z.string(),
  reservation_id: z.string(),
  guest_name: z.string().nullable(),
  channel: z.string().nullable(),
  /** Lifecycle status the server derived from the stay's own instants. */
  status: z.string(),
  /** Property-local days, `YYYY-MM-DD`. */
  check_in: z.string().nullable(),
  check_out: z.string().nullable(),
  /** UTC instants, null when the server cannot derive one. */
  check_in_at: z.string().nullable(),
  check_out_at: z.string().nullable(),
  nights: z.number().nullable(),
  guests: z.number().nullable(),
  total_payout: z.number().nullable(),
  /** Null when the thread could not be read: unknown, not "no". */
  needs_reply: z.boolean().nullable(),
})

const PropertyCalendarBlockSchema = z.object({
  id: z.string(),
  /** `owner`: made in the owner portal. `uplisting`: a hold made in Uplisting. */
  kind: z.enum(['owner', 'uplisting']),
  /** First blocked day, `YYYY-MM-DD`. */
  start: z.string(),
  /** EXCLUSIVE: the first day free again. */
  end_exclusive: z.string(),
  note: z.string().nullable(),
  /** Owner blocks only: whether Uplisting has the block yet. */
  sync_status: z.string().nullable(),
})

/**
 * The cleaning layer is the existing `CleaningJob` row, whole - the sheet's
 * actions need all of it. A job that does not parse turns the LAYER into an
 * error chip rather than failing the whole calendar read: stays and blocks are
 * still good, and a cleaning that cannot be read must not pass for none.
 */
const PropertyCalendarCleaningLayerSchema = calendarLayer(CleaningJobSchema).catch({
  status: 'error',
  message: 'Cleanings could not be read.',
})

/**
 * One deposit-eligible stay's whole timeline (admin-api CAP-874): recorded
 * events from the live record's history, planned ones from the server's own
 * deposit rules, oldest first. Events outside the visible range are included.
 * `type` is a plain string on purpose: a type the UI has not seen yet is shown
 * under its own name rather than failing the layer.
 */
const PropertyCalendarDepositEventSchema = z.object({
  type: z.string(),
  /** UTC instant, derived on the server. */
  at: z.string(),
  planned: z.boolean(),
})

const PropertyCalendarDepositSchema = z.object({
  reservation_id: z.string(),
  /** The live record's status, or `none` before any hold was attempted. */
  state: z.string(),
  amount_cents: z.number().nullable(),
  /** `uplisting` when Uplisting's own deposit feature holds the card. */
  held_by: z.string().nullable(),
  events: z.array(PropertyCalendarDepositEventSchema),
})

/** Same rule as the cleaning layer: an item that does not parse is an error chip, not a failed calendar. */
const PropertyCalendarDepositsLayerSchema = calendarLayer(PropertyCalendarDepositSchema).catch({
  status: 'error',
  message: 'Deposits could not be read.',
})

/**
 * The model's confidence in a supply estimate. Defined ahead of the supplies
 * section below because the calendar's supplies layer reads it too.
 */
export const SupplyConfidenceSchema = z.enum(['none', 'low', 'medium', 'high'])

/**
 * One category the supply model has a runout or a delivery for in the range
 * (admin-api CAP-877). Every date, quantity, estimate and sentence is the
 * model's own and is shown as returned. `last_delivery` is nullable and the
 * estimate can be null so a category with no history reads as such rather than
 * failing the layer.
 */
const PropertyCalendarSupplyDeliverySchema = z.object({
  /** `YYYY-MM-DD`. */
  date: z.string(),
  qty: z.number(),
  retailer: z.string().nullable(),
})

const PropertyCalendarSupplySchema = z.object({
  category: z.string(),
  /** The category's display name. */
  label: z.string(),
  /** The model's runout day, or null when it has no rate or the stock lasts. */
  runout_date: z.string().nullable(),
  /** Whether `runout_date` is inside the range: only those get a day marker. */
  runout_in_range: z.boolean(),
  confidence: SupplyConfidenceSchema,
  stock_estimate: z.number().nullable(),
  last_delivery: PropertyCalendarSupplyDeliverySchema.nullable(),
  /** Deliveries whose day is inside the range, oldest first. */
  deliveries: z.array(PropertyCalendarSupplyDeliverySchema),
  reasoning: z.string(),
})

/** Same rule as the other typed layers: an item that does not parse is an error chip, not a failed calendar. */
const PropertyCalendarSuppliesLayerSchema = calendarLayer(PropertyCalendarSupplySchema).catch({
  status: 'error',
  message: 'Supplies could not be read.',
})

/**
 * One stay's review window (admin-api CAP-879), read from the review chase.
 *
 * The days (`window_closes_on`, `guest_reviewed_on`, `host_reviewed_on`) are
 * property-local and the server's own: the closing day is Airbnb checkout plus
 * 14 and is null on every channel with no deadline. Two nulls mean different
 * things and must not be read alike:
 *
 * - `guest_reviewed_on` null is "no guest review SEEN", not "none": Airbnb
 *   withholds it from the host until both have reviewed or 14 days have passed.
 *   It does not depend on the chase.
 * - `has_chase_record` false (every stay before its window-open email, and one
 *   whose chase has expired) makes `status`, `skip_reason`, `host_reviewed_*`,
 *   `host_review_source`, both reminder lists and `thank_you_sent_at` UNKNOWN,
 *   not empty. A null host review there is not "you did not review".
 *
 * `status` and `host_review_source` stay plain strings so a value the UI has
 * not met is shown under its own name rather than failing the layer.
 */
const PropertyCalendarReviewSchema = z.object({
  reservation_id: z.string(),
  channel: z.string().nullable(),
  /** Property-local checkout day the window is counted from. */
  check_out: z.string().nullable(),
  window_closes_on: z.string().nullable(),
  has_chase_record: z.boolean(),
  /** `pending`, `review_received`, `skipped` or `expired`; null without a chase record. */
  status: z.string().nullable(),
  skip_reason: z.string().nullable(),
  guest_reviewed_first: z.boolean(),
  guest_reviewed_at: z.string().nullable(),
  guest_reviewed_on: z.string().nullable(),
  host_reviewed_at: z.string().nullable(),
  host_reviewed_on: z.string().nullable(),
  /** `v3` and `manual` were observed; `inferred` was deduced from Airbnb's reveal rule. */
  host_review_source: z.string().nullable(),
  /** UTC instants the operator opened the digest's reminder link (nothing is sent by that). */
  reminder_clicks: z.array(z.string()),
  /** UTC instants a reminder was actually sent from the admin portal. */
  reminder_sends: z.array(z.string()),
  thank_you_sent_at: z.string().nullable(),
})

/** Same rule as the other typed layers: an item that does not parse is an error chip, not a failed calendar. */
const PropertyCalendarReviewsLayerSchema = calendarLayer(PropertyCalendarReviewSchema).catch({
  status: 'error',
  message: 'Reviews could not be read.',
})

/**
 * The orphan-gaps layer (admin-api CAP-878): the property's stored scan, not a
 * live read. Unlike the other layers it always says which snapshot it speaks
 * for, because "no gaps" is a statement about a moment:
 *
 * - `ok`, `stale: false`: a current answer; empty `items` means no gaps.
 * - `ok`, `stale: true`: the daily scan did not refresh this property; the old
 *   items are listed and `as_of` says how old.
 * - `ok`, `incomplete: true`: the scan was cut short, so gaps may be missing.
 * - `error`: never scanned (`snapshot_missing`), the last scan failed
 *   (`snapshot_failed`) or the snapshot could not be read (`read_failed`). It
 *   must never pass for "no gaps".
 *
 * A gap is returned whole, even when it runs past the range; the two
 * `*_range` flags say so. The drafted message is for display only - nothing
 * here sends or records an offer. Strings that name a kind (`stage`,
 * `neighbour.kind`, `capped_by`) stay plain strings so a value the UI has not
 * seen yet is shown under its own name instead of failing the layer.
 */
const PropertyCalendarOrphanGapNightSchema = z.object({
  /** `YYYY-MM-DD`. */
  date: z.string(),
  market_nightly: z.number().nullable(),
  suggested_nightly: z.number(),
  /** `paid`: bounded by what the guest pays. `market`: bounded by what the night is worth. */
  capped_by: z.string(),
})

const PropertyCalendarOrphanGapStageSchema = z.object({
  /** `extend`: the guest leaving at the start of the gap. `arrive_earlier`: the guest arriving at its end. */
  stage: z.string(),
  neighbour: z.object({ kind: z.string(), id: z.string() }),
  reservation_id: z.string().nullable(),
  eligible: z.boolean(),
  /** Why not, in the server's words; null when eligible. */
  reason: z.string().nullable(),
  reason_label: z.string().nullable(),
  guest_name: z.string().nullable(),
  channel: z.string().nullable(),
  /** The drafted message, read-only. Null when the stage is not eligible. */
  message: z.string().nullable(),
  /** Null when the stage is not eligible: there is nothing to price. */
  pricing: z
    .object({
      paid_nightly: z.number(),
      discount_rate: z.number(),
      offer_value: z.number(),
      uniform: z.boolean(),
      nights: z.array(PropertyCalendarOrphanGapNightSchema),
    })
    .nullable(),
})

const PropertyCalendarOrphanGapSchema = z.object({
  gap_key: z.string(),
  /** First orphan night, `YYYY-MM-DD`. */
  first_night: z.string(),
  /** Last orphan night, INCLUSIVE (not a check-out-style exclusive end). */
  last_night: z.string(),
  nights: z.number(),
  min_stay: z.number(),
  actionable: z.boolean(),
  offer_value: z.number(),
  starts_before_range: z.boolean(),
  ends_after_range: z.boolean(),
  stages: z.array(PropertyCalendarOrphanGapStageSchema),
})

const PropertyCalendarOrphanGapsLayerSchema = z
  .discriminatedUnion('status', [
    z.object({
      status: z.literal('ok'),
      /** When the snapshot was computed, an ISO UTC instant. */
      as_of: z.string(),
      stale: z.boolean(),
      incomplete: z.boolean(),
      items: z.array(PropertyCalendarOrphanGapSchema),
    }),
    z.object({
      status: z.literal('error'),
      code: z.string(),
      message: z.string(),
      as_of: z.string().nullable(),
    }),
  ])
  .catch({
    status: 'error',
    code: 'unreadable',
    message: 'Orphan gaps could not be read.',
    as_of: null,
  })

/**
 * A property's calendar layers, the same block in the property calendar read
 * and in each row of the portfolio calendar read (`GET /admin/calendar`,
 * admin-api CAP-1414): one schema, so a layer can never parse one way on the
 * property tab and another on the portfolio.
 */
const PropertyCalendarLayersSchema = z
  .object({
    // A stay or block that does not parse turns its LAYER into an error chip,
    // as the cleaning layer does, rather than failing the whole read: on the
    // portfolio calendar that would blank every property for one bad row.
    stays: calendarLayer(PropertyCalendarStaySchema).catch({
      status: 'error',
      message: 'Stays could not be read.',
    }),
    blocks: calendarLayer(PropertyCalendarBlockSchema).catch({
      status: 'error',
      message: 'Blocks could not be read.',
    }),
    cleaning: PropertyCalendarCleaningLayerSchema,
    deposits: PropertyCalendarDepositsLayerSchema,
    supplies: PropertyCalendarSuppliesLayerSchema,
    reviews: PropertyCalendarReviewsLayerSchema,
    orphan_gaps: PropertyCalendarOrphanGapsLayerSchema,
  })
  .catchall(z.unknown())

const PropertyCalendarResponseSchema = z.object({
  property_id: z.string(),
  time_zone: z.string(),
  /** Inclusive property-local days. */
  from: z.string(),
  to: z.string(),
  generated_at: z.string(),
  layers: PropertyCalendarLayersSchema,
})

/**
 * `GET /admin/calendar?from&to` (admin-api CAP-1414): every active property's
 * calendar layers for one day range (at most 31 days), in the order the
 * properties list shows them. `name` is never empty: the Uplisting nickname,
 * or the server's own fallback. `thumbnail_url` is a small photo, or null when
 * the property has none.
 */
const PortfolioCalendarPropertySchema = z.object({
  property_id: z.string(),
  name: z.string(),
  thumbnail_url: z.string().nullable(),
  time_zone: z.string(),
  layers: PropertyCalendarLayersSchema,
})

const PortfolioCalendarResponseSchema = z.object({
  /** Inclusive days, echoed from the request. */
  from: z.string(),
  to: z.string(),
  generated_at: z.string(),
  properties: z.array(PortfolioCalendarPropertySchema),
})

export type PropertyCalendar = z.infer<typeof PropertyCalendarResponseSchema>
export type PropertyCalendarStay = z.infer<typeof PropertyCalendarStaySchema>
export type PropertyCalendarBlock = z.infer<typeof PropertyCalendarBlockSchema>
export type PropertyCalendarDeposit = z.infer<typeof PropertyCalendarDepositSchema>
export type PropertyCalendarDepositEvent = z.infer<typeof PropertyCalendarDepositEventSchema>
export type PropertyCalendarReview = z.infer<typeof PropertyCalendarReviewSchema>
export type PropertyCalendarSupply = z.infer<typeof PropertyCalendarSupplySchema>
export type PropertyCalendarSupplyDelivery = z.infer<typeof PropertyCalendarSupplyDeliverySchema>
export type PropertyCalendarOrphanGap = z.infer<typeof PropertyCalendarOrphanGapSchema>
export type PropertyCalendarOrphanGapStage = z.infer<typeof PropertyCalendarOrphanGapStageSchema>
export type PropertyCalendarOrphanGapsLayer = z.infer<typeof PropertyCalendarOrphanGapsLayerSchema>
export type PropertyCalendarLayers = z.infer<typeof PropertyCalendarLayersSchema>
export type PortfolioCalendar = z.infer<typeof PortfolioCalendarResponseSchema>
export type PortfolioCalendarProperty = z.infer<typeof PortfolioCalendarPropertySchema>

const PropertyExternalBlocksResponseSchema = z.object({
  propertyId: z.string(),
  blocks: z.array(PropertyExternalBlockSchema),
})

/**
 * An owner block as `GET /admin/properties/:id/blocks` returns it (admin-api
 * CAP-1379). Only what the Calendar tab reads is declared, and the ops-only
 * requester fields are left out on purpose: the panel never shows an email.
 */
export const OwnerBlockRecordSchema = z.object({
  block_id: z.string(),
  property_id: z.string().optional(),
  /** Inclusive first blocked day, `YYYY-MM-DD`. */
  start: z.string().optional(),
  /** EXCLUSIVE: the first day free again, `YYYY-MM-DD`. */
  end: z.string().optional(),
  note: z.string().optional(),
  cleaning_needed: z.boolean().optional(),
  created_by_name: z.string().nullable().optional(),
  sync_status: z.enum(['pending', 'synced', 'failed', 'deleting']).optional(),
  /** Only meaningful when `sync_status` is `failed`. */
  ambiguous: z.boolean().optional(),
  has_conflict: z.boolean().optional(),
  last_error: z.string().nullable().optional(),
  created_at: z.string().optional(),
  /** Set when the block was made without emailing the owner. */
  notify_owner: z.boolean().optional(),
})
export type OwnerBlockRecord = z.infer<typeof OwnerBlockRecordSchema>

/** A hold made in the Uplisting app: dates only, nothing here can change it. */
export const ExternalBlockRecordSchema = z.object({
  id: z.string(),
  start: z.string().optional(),
  /** EXCLUSIVE, like every other block end. */
  end: z.string().optional(),
})
export type ExternalBlockRecord = z.infer<typeof ExternalBlockRecordSchema>

const PropertyBlocksResponseSchema = z.object({
  property_id: z.string(),
  owner_blocks: z.array(OwnerBlockRecordSchema),
  external_blocks: z.array(ExternalBlockRecordSchema),
})
export type PropertyBlocks = z.infer<typeof PropertyBlocksResponseSchema>

const OwnerBlockWriteResponseSchema = z.object({ block: OwnerBlockRecordSchema.nullable() })
const CreateOwnerBlockResponseSchema = z.object({ block: OwnerBlockRecordSchema })

export interface CreateOwnerBlockInput {
  /** Client-made id: the same one on every retry of one attempt, a new one once the draft changes. */
  block_id: string
  start: string
  /** EXCLUSIVE checkout date, the portal's convention. */
  end: string
  note: string
  cleaning_needed: boolean
  cc_emails?: string[]
  /** False sends the owner no emails. The server's default is true. */
  notify_owner?: boolean
}

const ExternalBlockCheckSchema = z.object({
  nights: z.array(z.object({ date: z.string(), available: z.boolean().nullable() })),
  all_available: z.boolean(),
  any_blocked: z.boolean(),
})
export type ExternalBlockCheck = z.infer<typeof ExternalBlockCheckSchema>

const PropertyReservationsResponseWithExternalSchema = PropertyReservationsResponseSchema.extend({
  reservations: z.array(ReservationSummarySchema.extend({ external: z.unknown().optional() })),
})

/**
 * Yet another real contract gap (local Codex pre-PR review): properties.tsx
 * deliberately searches across `address`, `timezone`, `property_slug`, and
 * `external.uplisting.attributes` for its free-text property search (see
 * that file's own PropertyRow/PropertyExternalAttributes comment), but
 * PropertyListItemSchema in @natalan/capehost-contracts models none of
 * them -- safeParse() was silently stripping all four from the properties
 * list, breaking search for any property whose only match is one of these
 * fields.
 */
const PropertyListItemWithSearchFieldsSchema = PropertyListItemSchema.extend({
  address: z.unknown().optional(),
  timezone: z.string().optional(),
  property_slug: z.string().optional(),
  external: z.unknown().optional(),
  /**
   * Real contract gap (CAP-171 / admin-api PR #405, in review at the time
   * this was added): every row from GET /admin/properties now carries
   * `archived` and `managed_until` (property offboarding), but
   * PropertyListItemSchema in @natalan/capehost-contracts doesn't model
   * either yet -- without this extension safeParse() would silently strip
   * both, breaking the Archived tab and the archived-property badge before
   * they ever saw real data. `managed_until` is exclusive: the first day the
   * property is no longer managed (see api.archiveProperty below).
   */
  archived: z.boolean().optional(),
  managed_until: z.string().nullable().optional(),
})
const PropertiesListResponseWithSearchFieldsSchema = PropertiesListResponseSchema.extend({
  properties: z.array(PropertyListItemWithSearchFieldsSchema),
})

/**
 * Yet another real contract gap (local Codex pre-PR review): admin-api's
 * GET /admin/reservations/:id handler spreads a top-level `channel` field
 * onto the reservation (reservation-details.tsx reads `reservation.channel`
 * directly for the channel badge and Airbnb-specific review UI), but
 * ReservationSchema/GetReservationResponseSchema in
 * @natalan/capehost-contracts don't model it at all.
 */
const GetReservationResponseWithChannelSchema = GetReservationResponseSchema.extend({
  channel: z.string().nullable().optional(),
})

/**
 * Event payload passed to `regenerateSuggestion`'s `onEvent` callback.
 * Flattened (rather than a discriminated union keyed by event type) so it
 * stays structurally compatible with existing callers that only care about
 * a subset of fields per event (e.g. `useThreadOrchestration.ts`'s handler,
 * which only reads `data?.message` for the 'error' case) — every field is
 * only actually populated for its own event type; see the call sites below.
 */
interface RegenerateSuggestionEventData {
  suggestionId?: string
  version?: number
  status?: MessageSuggestionStatus
  text?: string
  message?: string
  unreachable?: boolean
  baseUrl?: string
}

/**
 * Schema for the raw JSON body read back from
 * POST /messages/:id/suggestions/regenerate. The real contract
 * (`RegenerateSuggestionResponseSchema`) is always `{ ok: true, suggestion,
 * trace_id }` on a 2xx, but the code below defensively reads `data.error` in
 * a branch that, per the contract, can never actually be reached when
 * `response.ok` is true. Validating against a union of the real schema and
 * this looser fallback shape preserves that defensive branch while still
 * giving the common-case response real runtime protection.
 */
const RegenerateSuggestionRawResponseSchema = z.union([
  RegenerateSuggestionResponseSchema,
  z.object({ ok: z.boolean().optional(), error: z.string().optional() }),
])

/**
 * Options accepted by `regenerateSuggestion`. Mirrors
 * `RegenerateSuggestionRequest` from the contracts package minus
 * `message_id` (a path param, not part of this options bag).
 */
interface RegenerateSuggestionOptions {
  instruction?: string
  instructions?: string
  mode?: string
  imageUrls?: string[]
}

const StripeLogSchema = z
  .object({
    id: z.string(),
    occurred_at: z.string(),
    source: z.string(),
    action: z.string(),
    level: z.string(),
    mode: z.string().nullable(),
    reservation_id: z.string().nullable(),
    property_id: z.string().nullable(),
    property_name: z.string().nullable(),
    payment_intent_id: z.string().nullable(),
    customer_id: z.string().nullable(),
    customer_email: z.string().nullable(),
    amount_cents: z.number().nullable(),
    currency: z.string().nullable(),
    message: z.string().nullable(),
  })
  .loose()

const StripeLogsResponseSchema = z.object({
  logs: z.array(StripeLogSchema),
  count: z.number(),
  nextCursor: z.string().nullable(),
})

export type StripeLog = z.infer<typeof StripeLogSchema>
export type StripeLogsResponse = z.infer<typeof StripeLogsResponseSchema>

/**
 * Which properties GET /admin/properties returns. Defaults to 'active' on the
 * backend when omitted, so `undefined` here must mean "don't send the param
 * at all" rather than sending `include=active` explicitly, to keep the
 * default request identical to what it was before this param existed.
 */
export type PropertiesInclude = 'active' | 'archived' | 'all'

/**
 * Property-archive (offboarding) response schemas.
 *
 * No published contract for this endpoint at all yet (same situation as the
 * security-deposit schemas above, and admin-api PR #405 is still in review) --
 * defined entirely locally until it moves into @natalan/capehost-contracts.
 * `other_properties[].archived` and `left_with_zero_active_properties` are the
 * two fields the affected-users prompt (property-details.tsx) actually
 * branches on.
 */
const ArchivePropertyOtherPropertySchema = z.object({
  id: z.string(),
  display_name: z.string(),
  archived: z.boolean(),
})

const ArchivePropertyAffectedUserSchema = z.object({
  user_id: z.string(),
  email: z.string(),
  other_properties: z.array(ArchivePropertyOtherPropertySchema),
  left_with_zero_active_properties: z.boolean(),
})

const ArchivePropertyResponseSchema = z.object({
  property_id: z.string(),
  managed_until: z.string(),
  offboarded_at: z.string(),
  offboard_reason: z.string().nullable(),
  affected_users: z.array(ArchivePropertyAffectedUserSchema),
})

export type ArchivePropertyOtherProperty = z.infer<typeof ArchivePropertyOtherPropertySchema>
export type ArchivePropertyAffectedUser = z.infer<typeof ArchivePropertyAffectedUserSchema>
export type ArchivePropertyResponse = z.infer<typeof ArchivePropertyResponseSchema>
/**
 * The Protection page (CAP-651). The deposit `state` is derived by admin-api,
 * never here: the UI must not infer business state.
 */
export const PROTECTION_DEPOSIT_STATES = [
  'scheduled',
  'hold_due',
  'held_by_uplisting',
  'held',
  'released',
  'captured',
  'no_card',
  'failed',
  'blocked',
  'needs_attention',
  'not_held',
  'not_required',
] as const

export type ProtectionDepositState = (typeof PROTECTION_DEPOSIT_STATES)[number]

const ProtectionDepositSchema = z
  .object({
    reservation_id: z.string(),
    guest_name: z.string().nullable(),
    property_id: z.string(),
    property_name: z.string().nullable(),
    channel: z.string().nullable(),
    check_in: z.string().nullable(),
    check_out: z.string().nullable(),
    cancelled: z.boolean(),
    property_enabled: z.boolean(),
    // A state added server-side later still renders (as its raw value)
    // rather than failing the whole list.
    state: z.string(),
    mode: z.string().nullable(),
    amount_cents: z.number(),
    captured_amount_cents: z.number().nullable(),
    refunded_amount_cents: z.number().nullable(),
    dispute_status: z.string().nullable(),
    hold_until: z.string().nullable().optional(),
    hold_until_reservation_id: z.string().nullable().optional(),
    payment_intent_id: z.string().nullable(),
    authorization_expires_at: z.string().nullable(),
    match_outcome: z.string().nullable(),
    last_error: z.string().nullable(),
    updated_at: z.string().nullable(),
    stripe_url: z.string().nullable(),
    history: z.array(
      z.object({ at: z.string(), action: z.string(), detail: z.string().nullable().optional() })
    ),
  })
  .loose()

const ProtectionDepositsResponseSchema = z.object({
  window: z.enum(['upcoming', 'past']),
  today: z.string(),
  automation_enabled: z.boolean(),
  count: z.number(),
  deposits: z.array(ProtectionDepositSchema),
})

const ProtectionSettingsResponseSchema = z.object({
  automation: SecurityDepositAutomationSchema,
  default_amount_cents: z.number(),
  placement_lead_hours: z.number(),
  coverage_after_checkout_hours: z.number(),
  max_hold_after_checkout_hours: z.number(),
  channels: z.array(
    z.object({
      channel: z.string(),
      label: z.string(),
      note: z.string().nullable(),
      enabled: z.boolean(),
    })
  ),
  listings: z.array(
    z.object({
      property_id: z.string(),
      name: z.string().nullable(),
      enabled: z.boolean(),
      amount_cents: z.number().nullable(),
      resolved_amount_cents: z.number(),
      updated_at: z.string().nullable(),
      updated_by: z.string().nullable(),
    })
  ),
  summary: z.object({ enabled_count: z.number(), total: z.number() }),
})

export type ProtectionDeposit = z.infer<typeof ProtectionDepositSchema>
export type ProtectionDepositsResponse = z.infer<typeof ProtectionDepositsResponseSchema>
export type ProtectionSettings = z.infer<typeof ProtectionSettingsResponseSchema>
export type { SecurityDepositAutomation, SecurityDepositConfig } from '@natalan/capehost-contracts'

// The refund, dispute (CAP-235) and hold-until (CAP-1286) fields are on every record admin-api
// serves, but the published record schema (4.8.0) does not name them yet. Declared here so the
// reservation card can show what the Protection drawer shows, and change the hold; drop the
// `.extend()` once contracts carries them (CAP-1020).
const ReservationSecurityDepositResponseSchema =
  ContractReservationSecurityDepositResponseSchema.extend({
    deposit: SecurityDepositRecordSchema.extend({
      refunded_amount_cents: z.number().nullable().optional(),
      dispute_id: z.string().nullable().optional(),
      dispute_status: z.string().nullable().optional(),
      dispute_due_by: z.string().nullable().optional(),
      hold_until: z.string().nullable().optional(),
      hold_until_reservation_id: z.string().nullable().optional(),
      property_id: z.string().nullable().optional(),
    }).nullable(),
  })
export type ReservationSecurityDeposit = z.infer<typeof ReservationSecurityDepositResponseSchema>
export type SecurityDepositRecord = NonNullable<ReservationSecurityDeposit['deposit']>

const SetDepositHoldUntilResponseSchema = z.object({
  reservation_id: z.string(),
  changed: z.boolean(),
  hold_until: z.string().nullable(),
  hold_until_reservation_id: z.string().nullable(),
  status: z.string(),
})

/**
 * API methods
 * All endpoints are under /admin/* prefix (protected by Cloudflare Access)
 */
// --- Supplies (CAP-464; admin-api src/routes/admin-supplies.ts) ---
// Every number here is admin-api's consumption model's (per guest-night, see
// admin-api docs/receipts-supplies-spike.md section 4.5). The page never
// computes a runout or a confidence itself.
export const SupplyDeliverySchema = z.object({
  receipt_id: z.string(),
  date: z.string(),
  qty: z.number(),
  retailer: z.string().nullable(),
  name_raw: z.string(),
})
export const SupplyCategorySchema = z.object({
  category: z.string(),
  deliveries: z.array(SupplyDeliverySchema),
  last_delivery: SupplyDeliverySchema,
  k: z.number(),
  property_rate: z.number().nullable(),
  portfolio_rate: z.number().nullable(),
  rate: z.number().nullable(),
  guest_nights_since_last: z.number(),
  guest_nights_ahead: z.number(),
  stock_estimate: z.number().nullable(),
  runout_date: z.string().nullable(),
  due_within_horizon: z.boolean(),
  confidence: SupplyConfidenceSchema,
  reasoning: z.string(),
})
export const PropertySuppliesResponseSchema = z.object({
  property_id: z.string(),
  today: z.string(),
  horizon_days: z.number(),
  stays_considered: z.number(),
  categories: z.array(SupplyCategorySchema),
  one_offs: z.array(
    z.object({
      receipt_id: z.string(),
      date: z.string(),
      name: z.string(),
      qty: z.number(),
      retailer: z.string().nullable(),
      source: z.string(),
    })
  ),
})
export const RestockResponseSchema = z.object({
  property_id: z.string(),
  today: z.string(),
  horizon_days: z.number(),
  adding: z.array(z.string()),
  suggestions: z.array(SupplyCategorySchema),
})
export const SupplyCycleSchema = z.object({
  from: z.string(),
  to: z.string(),
  qty: z.number(),
  guest_nights: z.number(),
  stays: z.number(),
  rate: z.number().nullable(),
})
export const SuppliesOverviewCellSchema = z.object({
  last_delivery_date: z.string(),
  last_qty: z.number(),
  last_receipt_id: z.string(),
  last_retailer: z.string().nullable(),
  runout_date: z.string().nullable(),
  due_within_horizon: z.boolean(),
  confidence: SupplyConfidenceSchema,
  rate: z.number().nullable(),
  stock_estimate: z.number().nullable(),
  guest_nights_ahead: z.number(),
  stays_ahead: z.number(),
  reasoning: z.string(),
  // The tooltip card's history and rate ingredients (CAP-481).
  deliveries: z.array(SupplyDeliverySchema),
  guest_nights_since_last: z.number(),
  k: z.number(),
  property_rate: z.number().nullable(),
  portfolio_rate: z.number().nullable(),
  // What the house's own rate is made of (CAP-486).
  cycles: z.array(SupplyCycleSchema),
  own_qty: z.number(),
  own_guest_nights: z.number(),
  own_stays: z.number(),
  vs_portfolio: z.number().nullable(),
})
export const PortfolioStatsSchema = z.object({
  rate: z.number(),
  qty: z.number(),
  guest_nights: z.number(),
  stays: z.number(),
  houses: z.number(),
  cycles: z.number(),
})
export const SuppliesForecastEntrySchema = SuppliesOverviewCellSchema.extend({
  property_id: z.string(),
  property_name: z.string(),
  category: z.string(),
})
export const UpcomingStaySchema = z.object({
  check_in: z.string(),
  check_out: z.string(),
  guests: z.number().nullable(),
})
export const SuppliesOverviewPropertySchema = z.object({
  property_id: z.string(),
  name: z.string(),
  categories: z.record(z.string(), SuppliesOverviewCellSchema),
  upcoming_stays: z.array(UpcomingStaySchema),
})
export const SuppliesOverviewResponseSchema = z.object({
  today: z.string(),
  horizon_days: z.number(),
  categories: z.array(z.string()),
  properties: z.array(SuppliesOverviewPropertySchema),
  forecast: z.array(SuppliesForecastEntrySchema),
  /** What each portfolio rate is made of, per category (CAP-486). */
  portfolio: z.record(z.string(), PortfolioStatsSchema),
})
// GET /admin/supplies/trends (CAP-473): deliveries per category by calendar
// month across the portfolio, next to the guest-nights those months carried.
export const SuppliesTrendMonthSchema = z.object({
  month: z.string(),
  qty: z.number(),
  deliveries: z.number(),
  spend: z.number(),
})
export const SuppliesTrendsResponseSchema = z.object({
  today: z.string(),
  months: z.array(z.string()),
  guest_nights: z.record(z.string(), z.number()),
  categories: z.array(
    z.object({
      category: z.string(),
      monthly: z.array(SuppliesTrendMonthSchema),
      qty: z.number(),
      deliveries: z.number(),
      spend: z.number(),
      houses: z.number(),
      portfolio_rate: z.number().nullable(),
    })
  ),
})
// Store receipts (admin-api src/routes/admin-receipts.ts) are modeled in
// @natalan/capehost-contracts since 4.6.0, including the asynchronous
// classification state (CAP-675, CAP-683).
// POST /admin/receipts/:id/attribute returns the updated receipt; the page
// only needs to know it landed, so the shape checked here is the minimum.
export const AttributeReceiptResponseSchema = z.object({
  receipt: z.object({ id: z.string(), property_id: z.string().nullable() }),
})
export type SupplyCategory = z.infer<typeof SupplyCategorySchema>
export type SupplyConfidence = z.infer<typeof SupplyConfidenceSchema>
export type PropertySuppliesResponse = z.infer<typeof PropertySuppliesResponseSchema>
export type RestockResponse = z.infer<typeof RestockResponseSchema>
export type SuppliesOverviewResponse = z.infer<typeof SuppliesOverviewResponseSchema>
export type SuppliesOverviewCell = z.infer<typeof SuppliesOverviewCellSchema>
export type SuppliesForecastEntry = z.infer<typeof SuppliesForecastEntrySchema>
export type SuppliesOverviewProperty = z.infer<typeof SuppliesOverviewPropertySchema>
export type PortfolioStats = z.infer<typeof PortfolioStatsSchema>
export type UpcomingStay = z.infer<typeof UpcomingStaySchema>
export type SuppliesTrendsResponse = z.infer<typeof SuppliesTrendsResponseSchema>
export type {
  Receipt,
  ReceiptItem,
  ReceiptArtifact,
  ReceiptClassificationStatus,
  ReclassifyReceiptsResponse,
  UnmatchedReceiptsResponse,
} from '@natalan/capehost-contracts'

/**
 * GET /admin/uplisting/oauth/status. Declared here rather than taken from
 * capehost-contracts because the contract predates `requested_scopes` and
 * `would_drop` (admin-api#883); drop this once a contracts release carries them.
 *
 * Both new fields are optional so a card rendered against an admin-api that has
 * not deployed them yet reads as "unknown" instead of "nothing would be dropped".
 */
export const UplistingConnectionStatusSchema = z.object({
  connected: z.boolean(),
  expires_at: z.string().optional(),
  /** Space-separated scopes on the stored token. Absent when the token response carried none. */
  scope: z.string().optional(),
  has_refresh_token: z.boolean().optional(),
  updated_at: z.string().optional(),
  /** What a re-consent would ask Uplisting for. */
  requested_scopes: z.array(z.string()).optional(),
  /** Granted scopes a re-consent would silently drop. */
  would_drop: z.array(z.string()).optional(),
})
export type UplistingConnectionStatus = z.infer<typeof UplistingConnectionStatusSchema>

export const api = {
  /**
   * Get properties. Defaults to active-only (admin-api's own default) when
   * `include` is omitted -- pass 'archived' or 'all' to see offboarded
   * properties too (see the Archived tab in properties.tsx).
   */
  getProperties: (include?: PropertiesInclude) =>
    apiRequest(
      `/admin/properties${include ? `?include=${include}` : ''}`,
      PropertiesListResponseWithSearchFieldsSchema
    ),

  /**
   * Get a single property by ID
   */
  getProperty: (propertyId: string) =>
    apiRequest(`/admin/properties/${propertyId}`, GetPropertyResponseWithExternalSchema),

  /**
   * Get reservations for a property
   */
  getPropertyReservations: (propertyId: string) =>
    apiRequest(
      `/admin/properties/${propertyId}/reservations`,
      PropertyReservationsResponseWithExternalSchema
    ),

  /**
   * A property's current external blocks - dates held directly in Uplisting's
   * dashboard rather than by a reservation of ours (CAP-172's read side).
   *
   * `end` is EXCLUSIVE, the same convention as a stay's check-out: a block
   * `2026-09-25` to `2026-09-27` holds the 25th and the 26th.
   *
   * No published contract models this yet, hence the local schema - same
   * pattern as the other admin-only reads above. Unknown block fields are
   * left unmodelled deliberately; only what a caller reads is declared.
   */
  getPropertyExternalBlocks: (propertyId: string) =>
    apiRequest(
      `/admin/uplisting/availability/external-blocks?propertyId=${encodeURIComponent(propertyId)}`,
      PropertyExternalBlocksResponseSchema
    ),

  /**
   * Every non-retired owner block of a property and the Uplisting-app holds
   * the reconcile has seen (admin-api CAP-1379): the full records the Calendar
   * tab's block panels read.
   */
  getPropertyBlocks: (propertyId: string) =>
    apiRequest(
      `/admin/properties/${encodeURIComponent(propertyId)}/blocks`,
      PropertyBlocksResponseSchema
    ),

  /**
   * Block dates for a property as an admin (admin-api CAP-1392). Idempotent by
   * `block_id`, so a lost response can be retried safely with the same body.
   */
  createOwnerBlock: (propertyId: string, input: CreateOwnerBlockInput) =>
    apiRequest(
      `/admin/properties/${encodeURIComponent(propertyId)}/owner-blocks`,
      CreateOwnerBlockResponseSchema,
      { method: 'POST', body: JSON.stringify(input) }
    ),

  /**
   * Remove an owner block. Always `mode: "release"`: it frees only the nights
   * no other block or booking covers. "forget" is API/CLI only, not offered here.
   */
  releaseOwnerBlock: (propertyId: string, blockId: string) =>
    apiRequest(
      `/admin/properties/${encodeURIComponent(propertyId)}/owner-blocks/${encodeURIComponent(blockId)}/remove`,
      OwnerBlockWriteResponseSchema,
      { method: 'POST', body: JSON.stringify({ mode: 'release', confirm: true }) }
    ),

  /** Live availability of `[start, end)` as Uplisting reports it right now. */
  checkExternalBlock: (propertyId: string, { start, end }: { start: string; end: string }) =>
    apiRequest(
      `/admin/properties/${encodeURIComponent(propertyId)}/external-blocks/check?start=${start}&end=${end}`,
      ExternalBlockCheckSchema
    ),

  /**
   * Get a property's calendar for a day range (at most 62 days): stays,
   * blocks and the other layers, each with its own ok/error status.
   */
  getPropertyCalendar: (propertyId: string, { from, to }: { from: string; to: string }) =>
    apiRequest(
      `/admin/properties/${encodeURIComponent(propertyId)}/calendar?from=${from}&to=${to}`,
      PropertyCalendarResponseSchema
    ),

  /**
   * Get every active property's calendar for a day range (at most 31 days),
   * one row per property: the portfolio calendar on `/`.
   */
  getPortfolioCalendar: (
    { from, to }: { from: string; to: string },
    { signal }: { signal?: AbortSignal } = {}
  ) =>
    apiRequest(
      `/admin/calendar?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      PortfolioCalendarResponseSchema,
      { signal }
    ),

  /**
   * Get PriceLabs nightly rates for a property's calendar month
   */
  getPropertyPricingCalendar: (
    propertyId: string,
    { year, month }: { year?: number; month?: number } = {}
  ) =>
    apiRequest(
      `/admin/properties/${propertyId}/pricing-calendar?year=${year}&month=${month}`,
      PropertyPricingCalendarResponseSchema
    ),

  /**
   * Get every guest review received for a property (both platforms), newest first
   */
  getPropertyReviews: (propertyId: string) =>
    apiRequest(
      `/admin/properties/${encodeURIComponent(propertyId)}/reviews`,
      PropertyReviewsResponseSchema
    ),

  /**
   * Manually trigger the Airbnb review scrape for a property (retries the
   * nightly enrichment on demand, e.g. after a failed run)
   */
  syncPropertyAirbnbReviews: (propertyId: string) =>
    apiRequest(
      `/admin/airbnb-reviews/sync?propertyId=${encodeURIComponent(propertyId)}`,
      SyncPropertyAirbnbReviewsResponseSchema,
      {
        method: 'POST',
      }
    ),

  /**
   * Get a single reservation by ID
   */
  getReservation: (reservationId: string) =>
    apiRequest(`/admin/reservations/${reservationId}`, GetReservationResponseWithChannelSchema),

  /**
   * Get thread for a reservation (1:1 relationship)
   * Returns null thread if no thread exists (backend returns 200 with null)
   */
  getReservationThread: (reservationId: string) =>
    apiRequest(`/admin/reservations/${reservationId}/thread`, GetReservationThreadResponseSchema),

  /**
   * Get messages for a thread
   */
  getThreadMessages: (threadId: string) =>
    apiRequest(`/admin/threads/${threadId}/messages`, ThreadMessagesResponseSchema),

  /**
   * Mark a thread as read. Pass observedMessageIds - the message_id of
   * every message actually fetched/displayed - so a message ingested after
   * that snapshot isn't silently marked read before the operator ever saw
   * it. Bounding by id rather than timestamp also can't be fooled by a
   * delayed/retried message stamped with an earlier occurred_at than the
   * snapshot's own messages.
   */
  markThreadRead: (
    threadId: string,
    observedMessageIds: MarkThreadReadRequest['observedMessageIds']
  ) =>
    apiRequest(`/admin/threads/${threadId}/read`, MarkThreadReadResponseSchema, {
      method: 'POST',
      body: JSON.stringify(
        observedMessageIds && observedMessageIds.length > 0 ? { observedMessageIds } : {}
      ),
    }),

  /**
   * Dismiss the "needs reply" nudge for a thread without sending anything -
   * for when the host has decided no reply is actually needed for the
   * guest's last message. A later guest message on the same thread needs a
   * reply again regardless of this call (root CLAUDE.md: "Do not show UI
   * controls that have no backend behavior behind them" - this is recorded
   * server-side against that exact message, not just hidden client-side).
   * @param {string} threadId - Thread ID
   * @returns {Promise<{ thread_id: string, needs_reply: false }>}
   */
  dismissThreadNeedsReply: (threadId: string) =>
    apiRequest(
      `/admin/threads/${threadId}/needs-reply/dismiss`,
      DismissThreadNeedsReplyResponseSchema,
      { method: 'POST' }
    ),

  /**
   * Get admin status (sync status, last sync info, pipeline health checks).
   */
  getAdminStatus: () => apiRequest('/admin/status', AdminStatusResponseSchema),

  /** Uplisting v3 OAuth connection: granted, requested and would-drop scopes. Never carries tokens. */
  getUplistingConnectionStatus: (signal?: AbortSignal) =>
    apiRequest('/admin/uplisting/oauth/status', UplistingConnectionStatusSchema, { signal }),

  /**
   * Where the re-consent flow starts. It lives on the API host (admin.capehost.ai
   * is the SPA and answers "No routes matched"), and it 302s to Uplisting's consent
   * screen, so it is navigated to rather than fetched.
   */
  getUplistingOAuthAuthorizeUrl: () => `${getApiBaseUrl()}/admin/uplisting/oauth/authorize`,

  /**
   * Sync properties from Uplisting
   */
  syncUplisting: () =>
    apiRequest('/admin/uplisting/sync', UplistingSyncResponseSchema, {
      method: 'POST',
    }),

  /**
   * Sync reservations for a property
   */
  syncPropertyReservations: (propertyId: string) =>
    apiRequest(
      `/admin/uplisting/sync/reservations?propertyId=${encodeURIComponent(propertyId)}`,
      UplistingReservationSyncResponseSchema,
      {
        method: 'POST',
      }
    ),

  /**
   * Get threads for a property (keyed by reservation_id)
   */
  getPropertyThreads: (propertyId: string) =>
    apiRequest(`/admin/properties/${propertyId}/threads`, PropertyThreadsResponseSchema),

  /**
   * Get messages for every thread on a property in one request, keyed by
   * thread id. Use instead of calling getThreadMessages once per thread.
   */
  getPropertyThreadsMessages: (propertyId: string) =>
    apiRequest(
      `/admin/properties/${propertyId}/threads/messages`,
      PropertyThreadsMessagesResponseSchema
    ),

  /**
   * Get a paginated page of a property's conversation threads, most
   * recently active first. Distinct from getPropertyThreads (which returns
   * everything unpaginated, keyed by reservation_id) - this is the new
   * endpoint backing the redesigned Messages inbox list.
   * @param {Object} options
   * @param {string} options.propertyId - Property ID (required)
   * @param {string} [options.cursor] - Opaque pagination cursor from a previous page's nextCursor
   * @param {number} [options.limit] - Max items per page
   * @param {boolean} [options.needsReply] - When true, ask the server to filter to threads
   *   whose last message is from the guest (unanswered). Filtering happens server-side, not
   *   over already-loaded pages.
   */
  getPropertyThreadsPage: ({
    propertyId,
    cursor,
    limit,
    needsReply,
  }: {
    propertyId: string
    cursor?: string
    limit?: number
    needsReply?: boolean
  }) => {
    const params = new URLSearchParams()
    if (cursor) {
      params.append('cursor', cursor)
    }
    if (limit) {
      params.append('limit', String(limit))
    }
    if (needsReply) {
      params.append('needsReply', 'true')
    }
    const qs = params.toString()
    return apiRequest(
      `/admin/properties/${propertyId}/threads/page${qs ? `?${qs}` : ''}`,
      PropertyThreadsPageResponseSchema
    )
  },

  /**
   * Get a paginated page of conversation threads across ALL properties,
   * most recently active first - backs the admin portal-wide Inbox.
   * Cross-property counterpart to getPropertyThreadsPage above; each item
   * also carries a `property` object (id/displayName/coverPhotoUrl) since
   * items span multiple properties.
   * @param {Object} [options]
   * @param {string} [options.cursor] - Opaque pagination cursor from a previous page's nextCursor
   * @param {number} [options.limit] - Max items per page
   * @param {boolean} [options.needsReply] - When true, ask the server to filter to threads
   *   whose last message is from the guest (unanswered). Filtering happens server-side, not
   *   over already-loaded pages.
   */
  getThreadsPage: ({
    cursor,
    limit,
    needsReply,
  }: {
    cursor?: string
    limit?: number
    needsReply?: boolean
  } = {}) => {
    const params = new URLSearchParams()
    if (cursor) {
      params.append('cursor', cursor)
    }
    if (limit) {
      params.append('limit', String(limit))
    }
    if (needsReply) {
      params.append('needsReply', 'true')
    }
    const qs = params.toString()
    return apiRequest(`/admin/threads/page${qs ? `?${qs}` : ''}`, ThreadsPageResponseSchema)
  },

  /**
   * Ask a question about a property, grounded in its ingested knowledge (guest
   * messages for now). Pass sessionId to continue a previous conversation.
   */
  askPropertyQuestion: (
    propertyId: string,
    {
      question,
      sessionId,
    }: { question: AskPropertyQuestionRequest['question']; sessionId?: string } = {
      question: '',
    }
  ) =>
    apiRequest(
      `/admin/properties/${encodeURIComponent(propertyId)}/ask`,
      AskPropertyQuestionResponseSchema,
      {
        method: 'POST',
        body: JSON.stringify({ question, sessionId }),
      }
    ),

  /**
   * List past ask-a-question sessions for a property, most recent first
   */
  getPropertyAskSessions: (propertyId: string) =>
    apiRequest(
      `/admin/properties/${encodeURIComponent(propertyId)}/ask/sessions`,
      ListPropertyAskSessionsResponseSchema
    ),

  /**
   * Get a single ask-a-question session's full message history
   */
  getPropertyAskSession: (propertyId: string, sessionId: string) =>
    apiRequest(
      `/admin/properties/${encodeURIComponent(propertyId)}/ask/sessions/${encodeURIComponent(sessionId)}`,
      GetPropertyAskSessionResponseSchema
    ),

  /**
   * Get the full conversation snapshot (guest name + messages) backing an
   * /ask citation's refId — the primary source behind an extracted Q&A pair.
   * sourceType is required by the backend — a thread id is only unique
   * within its own source type, not across every source a property has.
   */
  getPropertyKnowledgeThread: (propertyId: string, threadId: string, sourceType: string) =>
    apiRequest(
      `/admin/properties/${encodeURIComponent(propertyId)}/ask/threads/${encodeURIComponent(threadId)}?sourceType=${encodeURIComponent(sourceType)}`,
      GetPropertyKnowledgeThreadResponseSchema
    ),

  /**
   * Get thread and messages for a reservation
   */
  getReservationThreadWithMessages: (reservationId: string) =>
    apiRequest(`/admin/reservations/${reservationId}/thread`, GetReservationThreadResponseSchema),

  /**
   * Get property external ID mappings
   */
  getPropertyExternalIds: (propertyId: string) =>
    apiRequest(
      `/admin/properties/${propertyId}/external-ids`,
      GetPropertyExternalIdsResponseSchema
    ),

  /**
   * Update property (including external ID mappings)
   */
  updateProperty: (propertyId: string, propertyData: Omit<UpdatePropertyRequest, 'id'>) =>
    apiRequest(`/admin/properties/${propertyId}`, UpdatePropertyResponseSchema, {
      method: 'PUT',
      body: JSON.stringify(propertyData),
    }),

  /**
   * Archive a property (offboarding): stop managing it as of `managed_until`,
   * an EXCLUSIVE boundary -- the first day no longer managed, which in
   * practice equals the day after the last guest's checkout. There is no
   * unarchive flow (confirmed from admin-api's endpoint), so this cannot be
   * undone from the UI.
   *
   * The response's `affected_users` lists every user who has this property
   * assigned, each flagged with `left_with_zero_active_properties` when this
   * archive leaves them with no active property left -- deactivating any of
   * them is a separate, explicit operator action (see api.upsertUser), never
   * implied by this call.
   */
  archiveProperty: (
    propertyId: string,
    { managed_until, reason }: { managed_until: string; reason?: string }
  ) =>
    apiRequest(
      `/admin/properties/${encodeURIComponent(propertyId)}/archive`,
      ArchivePropertyResponseSchema,
      {
        method: 'POST',
        body: JSON.stringify(reason ? { managed_until, reason } : { managed_until }),
      }
    ),

  /**
   * Update property external ID mappings
   * @deprecated Use updateProperty instead
   */
  updatePropertyExternalIds: (
    propertyId: string,
    mappings: Omit<UpdatePropertyExternalIdsRequest, 'id'>
  ) =>
    apiRequest(
      `/admin/properties/${propertyId}/external-ids`,
      UpdatePropertyExternalIdsResponseSchema,
      {
        method: 'PUT',
        body: JSON.stringify(mappings),
      }
    ),

  /**
   * List feature flag definitions annotated with this property's effective state.
   * @param {string} propertyId
   * @returns {Promise<{ property_id: string, flags: Array<{ key: string, label: string, description: string, default: boolean, enabled: boolean, overridden: boolean }>, count: number, updated_at: string | null, updated_by: string | null }>}
   */
  getPropertyFeatures: (propertyId: string) =>
    apiRequest(
      `/admin/properties/${encodeURIComponent(propertyId)}/settings/features`,
      AdminPropertyFeaturesListResponseSchema
    ),

  /**
   * Set or clear a feature flag override for a property.
   *
   * - `enabled = true | false` writes an explicit override
   * - `enabled = null` clears the override (reverts to registry default)
   *
   * @param {string} propertyId
   * @param {string} key
   * @param {boolean | null} enabled
   */
  setPropertyFeature: (propertyId: string, key: string, enabled: boolean | null) =>
    apiRequest(
      `/admin/properties/${encodeURIComponent(propertyId)}/settings/features/${encodeURIComponent(key)}`,
      AdminPropertyFeatureUpdateResponseSchema,
      {
        method: 'PATCH',
        body: JSON.stringify({ enabled }),
      }
    ),

  /**
   * Get reservation external ID mappings
   */
  getReservationExternalIds: (reservationId: string) =>
    apiRequest(
      `/admin/reservations/${reservationId}/external-ids`,
      GetReservationExternalIdsResponseSchema
    ),

  /**
   * Update reservation external ID mappings
   */
  updateReservationExternalIds: (
    reservationId: string,
    mappings: Omit<UpdateReservationExternalIdsRequest, 'id'>
  ) =>
    apiRequest(
      `/admin/reservations/${reservationId}/external-ids`,
      UpdateReservationExternalIdsResponseSchema,
      {
        method: 'PUT',
        body: JSON.stringify(mappings),
      }
    ),

  /**
   * List portal/admin users (for access control configuration)
   */
  listUsers: () => apiRequest('/admin/users', ListUsersResponseSchema),

  /**
   * Create or update a user by email
   */
  upsertUser: ({ email, first_name, last_name, role, status }: UpsertUserRequest) =>
    apiRequest('/admin/users', UpsertUserResponseSchema, {
      method: 'POST',
      body: JSON.stringify({ email, first_name, last_name, role, status }),
    }),

  /**
   * Invite user: local record + Auth0 user/link + password setup email
   */
  inviteUser: ({ email, role, property_ids, name, first_name, last_name }: InviteUserRequest) =>
    apiRequest('/admin/users/invite', InviteUserResponseSchema, {
      method: 'POST',
      body: JSON.stringify({
        email,
        role,
        property_ids,
        name,
        first_name,
        last_name,
      }),
    }),

  /**
   * Resend Auth0 password setup email
   */
  resendUserInvite: (userId: string) =>
    apiRequest(
      `/admin/users/${encodeURIComponent(userId)}/resend-invite`,
      ResendInviteResponseSchema,
      {
        method: 'POST',
      }
    ),

  /**
   * Get a single user with property assignments
   */
  getUser: (userId: string) =>
    apiRequest(`/admin/users/${encodeURIComponent(userId)}`, GetUserResponseSchema),

  /**
   * Start a "view/act as owner" portal impersonation session for this owner. Returns a
   * `portalUrl` (portal.capehost.ai deep link carrying the session token) to open in a new
   * tab — see admin-api's routes/portal-impersonation.ts.
   */
  startPortalImpersonation: (ownerUserId: string) =>
    apiRequest('/admin/portal/impersonation', StartPortalImpersonationResponseSchema, {
      method: 'POST',
      body: JSON.stringify({ ownerUserId }),
    }),

  /** End an impersonation session early (also self-expires after its TTL). */
  revokePortalImpersonation: (impersonationId: string) =>
    apiRequest(
      `/admin/portal/impersonation/${encodeURIComponent(impersonationId)}/revoke`,
      RevokePortalImpersonationResponseSchema,
      { method: 'POST' }
    ),

  /**
   * Update user preferences (notify_new_reports_published)
   */
  patchUser: (userId: string, patch: Omit<PatchUserRequest, 'id'>) =>
    apiRequest(`/admin/users/${encodeURIComponent(userId)}`, PatchUserResponseSchema, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  /**
   * Delete a user
   */
  deleteUser: (userId: string) =>
    apiRequest(`/admin/users/${encodeURIComponent(userId)}`, DeleteUserResponseSchema, {
      method: 'DELETE',
    }),

  /**
   * Get property IDs assigned to a user
   */
  getUserProperties: (userId: string) =>
    apiRequest(
      `/admin/users/${encodeURIComponent(userId)}/properties`,
      GetUserPropertiesResponseSchema
    ),

  /**
   * Replace property IDs assigned to a user
   */
  setUserProperties: (userId: string, propertyIds: string[]) =>
    apiRequest(
      `/admin/users/${encodeURIComponent(userId)}/properties`,
      SetUserPropertiesResponseSchema,
      {
        method: 'PUT',
        body: JSON.stringify({ property_ids: propertyIds }),
      }
    ),

  /**
   * Trigger report email dispatch for the current period
   */
  triggerReportEmail: () =>
    apiRequest('/admin/reports/trigger-report-email', TriggerReportEmailResponseSchema, {
      method: 'POST',
    }),

  /**
   * List past report email send records
   * @param {{ propertyId?: string, period_start?: string }} [params]
   */
  getReportEmailHistory: (params: Partial<ReportEmailHistoryQuery> = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).filter((entry): entry is [string, string] => entry[1] != null)
      )
    ).toString()
    return apiRequest(
      `/admin/reports/email-history${qs ? `?${qs}` : ''}`,
      ReportEmailHistoryResponseSchema
    )
  },

  /**
   * Delete a single report email dedup record so the email can be re-sent
   * @param {{ period_start: string, propertyId: string, userId: string }} params
   */
  deleteReportEmailRecord: ({
    period_start,
    propertyId,
    userId,
  }: {
    period_start: string
    propertyId: string
    userId: string
  }) =>
    apiRequest(
      `/admin/reports/email-history?period_start=${encodeURIComponent(period_start)}&propertyId=${encodeURIComponent(propertyId)}&userId=${encodeURIComponent(userId)}`,
      DeleteReportEmailRecordResponseSchema,
      { method: 'DELETE' }
    ),

  /**
   * The PDF of a report email exactly as it was sent to that recipient
   * (CAP-608). admin-api renders it from its archived copy on first request.
   *
   * Fetched rather than linked, like `getCleaningAckProof`: admin-ui and the
   * API are different origins, so a plain link would reach Cloudflare Access
   * without this tab's session.
   */
  getReportEmailPdf: async ({
    period_start,
    propertyId,
    userId,
  }: {
    period_start: string
    propertyId: string
    userId: string
  }): Promise<{ blob: Blob; filename: string | null }> => {
    const qs = new URLSearchParams({ period_start, propertyId, userId }).toString()
    const res = await fetch(`${getApiBaseUrl()}/admin/reports/email-history/archive/pdf?${qs}`, {
      credentials: 'include',
    })
    if (!res.ok) throw new Error(`Could not load the report PDF (${res.status})`)
    // admin-api names the file (street address and period, e.g. "329 Swift
    // Ave - Sep 13-19, 2026.pdf"), so the download matches the CLI's.
    const filename =
      /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? null
    return { blob: await res.blob(), filename }
  },

  /**
   * Generate email preview HTML for a property without sending. When a userId is
   * given, the preview renders on behalf of that user — net vs gross per their
   * reporting_mode. Nothing is ever sent.
   * @param {string} propertyId
   * @param {string} [userId]
   */
  getReportEmailPreview: (propertyId: string, userId?: string) =>
    apiRequest(
      `/admin/reports/email-preview?propertyId=${encodeURIComponent(propertyId)}${
        userId ? `&userId=${encodeURIComponent(userId)}` : ''
      }`,
      ReportEmailPreviewResponseSchema
    ),

  /**
   * List registered newsletter issues (newest first) and the latest slug.
   * @returns {Promise<{ issues: Array<{slug: string, subject: string, period_label: string}>, latest_slug: string|null }>}
   */
  listNewsletters: () => apiRequest('/admin/newsletters', ListNewslettersResponseSchema),

  /**
   * Render the current monthly owner newsletter without sending it.
   * @param {string} slug
   */
  getNewsletterPreview: (slug: string) =>
    apiRequest(
      `/admin/newsletters/${encodeURIComponent(slug)}/preview`,
      NewsletterPreviewResponseSchema
    ),

  /**
   * Send one test copy of the newsletter to an arbitrary address. Required
   * before send-to-all will unlock (server-side safeguard, see admin-api).
   * @param {string} slug
   * @param {string} email
   */
  sendNewsletterTest: (slug: string, email: string) =>
    apiRequest(
      `/admin/newsletters/${encodeURIComponent(slug)}/send-test`,
      NewsletterSendTestResponseSchema,
      {
        method: 'POST',
        body: JSON.stringify({ email }),
      }
    ),

  /**
   * Send the newsletter to every eligible owner. The backend rejects this
   * (409) unless a successful test send happened within the last hour.
   * @param {string} slug
   */
  sendNewsletterToAllOwners: (slug: string) =>
    apiRequest(
      `/admin/newsletters/${encodeURIComponent(slug)}/send`,
      NewsletterSendAllResponseSchema,
      {
        method: 'POST',
        body: JSON.stringify({ confirm: true }),
      }
    ),

  /**
   * Get a property's economic config: per-property override, global defaults, and
   * the resolved (effective) values. Used by the commission editor.
   * @param {string} propertyId
   * @returns {Promise<{ property_id: string, override: object|null, defaults: object, resolved: object, updated_at: string|null }>}
   */
  getPropertyEconomics: (propertyId: string) =>
    apiRequest(
      `/admin/properties/${encodeURIComponent(propertyId)}/settings/economic`,
      GetPropertyEconomicsResponseSchema
    ),

  /**
   * Update a property's economic override. Set a field to null to clear it (revert
   * to the global default).
   * @param {string} propertyId
   * @param {Record<string, unknown>} patch
   */
  updatePropertyEconomics: (
    propertyId: string,
    patch: Omit<UpdatePropertyEconomicsRequest, 'id'>
  ) =>
    apiRequest(
      `/admin/properties/${encodeURIComponent(propertyId)}/settings/economic`,
      UpdatePropertyEconomicsResponseSchema,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      }
    ),

  listPulseIngestRuns: () =>
    apiRequest('/admin/pulse/ingest-runs', ListPulseIngestRunsResponseSchema),

  reingestPulse: (
    r2Key: ReingestPulseRequest['r2_key'],
    asOfDate: ReingestPulseRequest['as_of_date']
  ) =>
    apiRequest('/admin/pulse/reingest', ReingestPulseResponseSchema, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ r2_key: r2Key, as_of_date: asOfDate }),
    }),

  /**
   * Get suggestions for a specific message
   */
  getMessageSuggestions: (messageId: string) =>
    apiRequest(`/admin/messages/${messageId}/suggestions`, GetMessageSuggestionsResponseSchema),

  /**
   * Regenerate a suggestion for a message
   * Backend returns queued suggestion immediately, actual generation happens async
   * Polling will pick up the completed suggestion when ready
   * @param {string} messageId - Message ID
   * @param {Object} options - Generation options
   * @param {string} options.instruction - Optional instruction (singular)
   * @param {string} options.instructions - Optional instructions (plural, for backward compat)
   * @param {string} options.mode - 'generate' or 'regenerate'
   * @param {Function} onEvent - Callback for events (meta, done, error)
   * @returns {Promise<void>}
   */
  regenerateSuggestion: async (
    messageId: string,
    options: RegenerateSuggestionOptions = {},
    onEvent?: (eventType: 'meta' | 'done' | 'error', data: RegenerateSuggestionEventData) => void
  ): Promise<void> => {
    if (!onEvent) {
      throw new Error('onEvent callback is required')
    }

    const baseUrl = getApiBaseUrl()
    const url = `${baseUrl}/admin/messages/${messageId}/suggestions/regenerate`

    let response: Response
    try {
      response = await fetchApi(
        url,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            instruction: options.instruction || options.instructions || '',
            mode: options.mode || 'generate',
            imageUrls: options.imageUrls || [],
          }),
        },
        baseUrl
      )
    } catch (err) {
      if (err instanceof ApiUnreachableError) {
        onEvent('error', { message: err.message, unreachable: true, baseUrl: err.baseUrl })
        return
      }
      throw err
    }

    if (!response.ok) {
      const errorText = await response.text()
      onEvent('error', {
        message: `Suggestion generation failed: ${response.status} ${response.statusText}${errorText ? ` - ${errorText}` : ''}`,
      })
      return
    }

    const rawData: unknown = await response.json()
    const parsedRaw = RegenerateSuggestionRawResponseSchema.safeParse(rawData)
    if (!parsedRaw.success) {
      throw new ResponseValidationError(
        '/admin/messages/:id/suggestions/regenerate',
        parsedRaw.error.issues
      )
    }
    const data = parsedRaw.data

    if ('suggestion' in data && data.suggestion) {
      // Emit meta event with suggestion info so UI can update
      onEvent('meta', {
        suggestionId: data.suggestion.id,
        version: data.suggestion.version,
        status: data.suggestion.status,
      })
      // Emit done event - polling will handle fetching the completed suggestion
      // The suggestion is queued/streaming, so text may be empty initially
      onEvent('done', {
        text: data.suggestion.text || '',
        status: data.suggestion.status,
      })
    } else {
      onEvent('error', { message: ('error' in data && data.error) || 'Unknown error' })
    }
  },

  /**
   * Reprocess a message from its original email KV data
   * @param {string} messageId - Message ID
   * @returns {Promise<Object>} - Updated message object
   */
  reprocessMessage: (messageId: string) =>
    apiRequest(`/admin/messages/${messageId}/reprocess`, ReprocessMessageResponseSchema, {
      method: 'POST',
    }),

  /**
   * Upload an image to R2 storage
   * @param {File} imageFile - Image file to upload
   * @returns {Promise<{url: string}>} - Image URL
   */
  uploadImage: async (imageFile: File) => {
    const formData = new FormData()
    formData.append('image', imageFile)

    const baseUrl = getApiBaseUrl()
    const url = `${baseUrl}/admin/images/upload`
    const response = await fetchApi(
      url,
      {
        method: 'POST',
        credentials: 'include',
        body: formData,
      },
      baseUrl
    )

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(
        `Image upload failed: ${response.status} ${response.statusText}${errorText ? ` - ${errorText}` : ''}`
      )
    }

    const data: unknown = await response.json()
    const result = UploadImageResponseSchema.safeParse(data)
    if (!result.success) {
      throw new ResponseValidationError('/admin/images/upload', result.error.issues)
    }
    return result.data
  },

  /**
   * Re-assign a message to a different property and/or reservation
   * @param {string} messageId - Message ID to re-assign
   * @param {Object} assignment - Assignment data
   * @param {string} assignment.property_id - Property ID (required)
   * @param {string|null} assignment.reservation_id - Reservation ID (optional, null for no reservation)
   * @returns {Promise<Object>} - Updated message object
   */
  reassignMessage: (messageId: string, assignment: Omit<ReassignMessageRequest, 'messageId'>) => {
    // URL encode messageId to handle special characters like @ and .
    const encodedMessageId = encodeURIComponent(messageId)
    const endpoint = `/admin/messages/${encodedMessageId}/reassign`
    return apiRequest(endpoint, ReassignMessageResponseSchema, {
      method: 'POST',
      body: JSON.stringify(assignment),
    })
  },

  /**
   * Submit feedback for a suggestion
   * @param {string} messageId - Message ID
   * @param {number} version - Suggestion version
   * @param {Object} feedback - Feedback data
   * @param {'up'|'down'|null} feedback.rating - Rating (up, down, or null to clear)
   * @param {string|null} feedback.justification - Optional justification text (for thumbs down)
   * @returns {Promise<Object>} - Updated suggestion with feedback
   */
  submitSuggestionFeedback: (
    messageId: string,
    version: number,
    feedback: Omit<SuggestionFeedbackRequest, 'message_id' | 'version'>
  ) => {
    const encodedMessageId = encodeURIComponent(messageId)
    const endpoint = `/admin/messages/${encodedMessageId}/suggestions/${version}/feedback`
    return apiRequest(endpoint, SuggestionFeedbackResponseSchema, {
      method: 'POST',
      body: JSON.stringify(feedback),
    })
  },

  /**
   * Send a suggestion's text as a real guest message via Uplisting. Never
   * called automatically — always a direct operator action (see root
   * CLAUDE.md's "Reply suggestions: operators always review before
   * sending — system never auto-sends").
   * @param {string} messageId - Message ID
   * @param {number} version - Suggestion version
   * @param {Object} params
   * @param {string} params.body - Final text to send (may differ from the
   *   suggestion's own text if the operator edited it)
   * @param {string} params.idempotencyKey - Caller-generated UUID, unique
   *   per Send click — the backend uses this to guarantee a retried
   *   request never sends the message twice
   * @returns {Promise<{ sent: true, auditId: string, uplistingMessageId: string|null }>}
   */
  sendSuggestionMessage: (
    messageId: string,
    version: number,
    { body, idempotencyKey }: Pick<SendSuggestionMessageRequest, 'body' | 'idempotencyKey'>
  ) => {
    const encodedMessageId = encodeURIComponent(messageId)
    const endpoint = `/admin/messages/${encodedMessageId}/suggestions/${version}/send`
    return apiRequest(endpoint, SendSuggestionMessageResponseSchema, {
      method: 'POST',
      body: JSON.stringify({ body, idempotencyKey }),
    })
  },

  /**
   * Get AI settings
   * @returns {Promise<Object>} - AI settings object
   */
  getAISettings: () => apiRequest('/admin/ai-settings', AiSettingsResponseSchema),

  /**
   * Update AI settings
   * @param {Object} settings - AI settings to update
   * @param {string} settings.model - Model name (e.g., "gpt-4o-mini")
   * @param {number} settings.max_completion_tokens - Max completion tokens
   * @param {number|null} settings.temperature - Temperature (0-2, or null to not set)
   * @param {string} settings.system_prompt - System prompt for AI behavior
   * @param {string} settings.suggestion_prompt - Suggestion prompt template (supports {{variables}})
   * @returns {Promise<Object>} - Updated AI settings
   */
  updateAISettings: (settings: UpdateAiSettingsRequest) =>
    apiRequest('/admin/ai-settings', UpdateAiSettingsResponseSchema, {
      method: 'PUT',
      body: JSON.stringify(settings),
    }),

  /**
   * Run migration to seed default prompts into KV storage
   * @returns {Promise<Object>} - Migration result with success, message, and updated status
   */
  runSeedPromptsMigration: () =>
    apiRequest('/admin/migrations/seed-prompts', SeedPromptsMigrationResponseSchema, {
      method: 'POST',
    }),

  /**
   * Get orphaned messages (missing property_id or reservation_id)
   * @param {Object} options - Query options
   * @param {string} options.missing - Filter by missing field: 'property'|'reservation'|'both'|'any'
   * @param {number} options.limit - Maximum number of messages to return
   * @param {string} options.cursor - Pagination cursor
   * @returns {Promise<{items: Object[], nextCursor: string|null, count: number}>} - Orphaned messages
   */
  getOrphanedMessages: (options: Partial<ListOrphanedMessagesRequest> = {}) => {
    const params = new URLSearchParams()
    if (options.missing) params.append('missing', options.missing)
    if (options.limit) params.append('limit', String(options.limit))
    if (options.cursor) params.append('cursor', options.cursor)
    const queryString = params.toString()
    return apiRequest(
      `/admin/messages/orphaned${queryString ? `?${queryString}` : ''}`,
      ListOrphanedMessagesResponseSchema
    )
  },

  /**
   * Assign property and/or reservation to an orphaned message
   * Uses the reassign endpoint which handles both assign and reassign cases
   * @param {string} messageId - Message ID
   * @param {Object} assignment - Assignment data
   * @param {string|null} assignment.property_id - Property ID (optional, will be inferred from reservation if not provided)
   * @param {string|null} assignment.reservation_id - Reservation ID (optional)
   * @returns {Promise<Object>} - Updated message object
   */
  assignOrphanedMessage: (
    messageId: string,
    assignment: Omit<ReassignMessageRequest, 'messageId'>
  ) => {
    const encodedMessageId = encodeURIComponent(messageId)
    // Use reassign endpoint which now handles both assign and reassign cases
    return apiRequest(
      `/admin/messages/${encodedMessageId}/reassign`,
      ReassignMessageResponseSchema,
      {
        method: 'POST',
        body: JSON.stringify(assignment),
      }
    )
  },

  /**
   * Delete an orphaned message
   * @param {string} messageId - Message ID
   * @returns {Promise<{deleted: boolean, message_id: string, email_deleted: boolean}>} - Deletion result
   */
  deleteOrphanedMessage: (messageId: string) => {
    const encodedMessageId = encodeURIComponent(messageId)
    return apiRequest(`/admin/messages/${encodedMessageId}`, DeleteOrphanedMessageResponseSchema, {
      method: 'DELETE',
    })
  },

  /**
   * List image assets from R2 (capehost-images) via Worker
   * @param {Object} [options]
   * @param {string} [options.prefix] - Object key prefix filter
   * @param {string} [options.cursor] - Pagination cursor from previous response
   * @param {number} [options.limit] - Page size (max 1000)
   * @returns {Promise<{ items: Array<{ objectKey: string, fileName: string, url: string, publicUrl?: string, size: number, uploaded: string|null }>, cursor: string|null, truncated: boolean, bucket: string }>}
   */
  getLogoAssets: (options: { prefix?: string; cursor?: string; limit?: number } = {}) => {
    const params = new URLSearchParams()
    if (options.prefix) {
      params.append('prefix', options.prefix)
    }
    if (options.cursor) {
      params.append('cursor', options.cursor)
    }
    if (options.limit) {
      params.append('limit', String(options.limit))
    }
    const qs = params.toString()
    return apiRequest(`/admin/assets/logos${qs ? `?${qs}` : ''}`, ListLogoAssetsResponseSchema)
  },

  /**
   * Orphan-gap offer candidates for ONE property.
   *
   * Deliberately per-property, mirroring the endpoint: admin-api evaluates a
   * single property per call (see its LIMITATIONS.md entry on why a
   * portfolio-wide mode was removed), so callers wanting the whole portfolio
   * iterate. The page does that sequentially rather than fanning out — each
   * call can touch a year of PriceLabs months, and twenty at once would
   * rate-limit the provider, which surfaces as withheld gaps rather than as an
   * error.
   */
  getOrphanGapOffers: (propertyId: string) =>
    apiRequest(
      `/admin/orphan-offers?propertyId=${encodeURIComponent(propertyId)}`,
      OrphanGapOffersResponseSchema
    ),

  /**
   * The precomputed portfolio snapshot — what the Orphan Nights page renders.
   *
   * One request, a handful of KV reads on the server, no PriceLabs. The page
   * used to call `getOrphanGapOffers` once per property instead and the
   * operator waited through a year of calendar fetches for an answer that
   * changes about once a day.
   */
  getOrphanGapSnapshot: () =>
    apiRequest('/admin/orphan-offers/snapshot', OrphanGapSnapshotResponseSchema),

  /**
   * Start a fresh scan. Returns as soon as the per-property jobs are enqueued;
   * the caller watches `running`/`counts.pending` on the snapshot above.
   */
  scanOrphanGaps: () =>
    apiRequest('/admin/orphan-offers/scan', OrphanGapScanResponseSchema, { method: 'POST' }),

  getReviewChaseDigest: () =>
    apiRequest('/admin/review-chase/digest', ReviewChaseDigestResponseWithSuggestionsSchema),

  /** Everything the Remind dialog shows before a send (CAP-301). */
  getRemindPreflight: (platform: string, externalId: string) => {
    const params = new URLSearchParams({ platform, external_id: externalId })
    return apiRequest(
      `/admin/review-chase/remind/preflight?${params.toString()}`,
      RemindPreflightSchema
    )
  },

  /**
   * Send the review reminder through the guarded Uplisting path. 409 with
   * body.code "needs_override" when a reminder already went out and
   * `override` was not set; 422 with body.blocked when it cannot be sent.
   */
  sendReminder: (
    platform: string,
    externalId: string,
    body: { message: string; idempotencyKey: string; override?: boolean }
  ) => {
    const params = new URLSearchParams({ platform, external_id: externalId })
    return apiRequest(
      `/admin/review-chase/remind/send?${params.toString()}`,
      RemindSendResponseSchema,
      {
        method: 'POST',
        body: JSON.stringify(body),
      }
    )
  },

  /** Record that the host left their review of this guest (VRBO has no feed for it). */
  markHostReviewed: (platform: string, externalId: string) => {
    const params = new URLSearchParams({ platform, external_id: externalId })
    return apiRequest(
      `/admin/review-chase/host-reviewed?${params.toString()}`,
      HostReviewedResponseSchema,
      {
        method: 'POST',
      }
    )
  },

  skipReviewChase: ({
    platform,
    externalId,
    reason,
  }: {
    platform: string
    externalId: string
    reason?: string
  }) => {
    const params = new URLSearchParams({ platform, external_id: externalId })
    return apiRequest(
      `/admin/review-chase/skip-reason?${params.toString()}`,
      ReviewChaseSkipReasonResponseSchema,
      {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }
    )
  },

  /**
   * Get the latest AI-drafted guest review suggestion for a reservation.
   * Returns { suggestion: null } if generation hasn't run (or finished) yet.
   * @param {string} reservationId - Reservation ID
   */
  getReviewSuggestion: (reservationId: string) =>
    apiRequest(
      `/admin/reservations/${reservationId}/review-suggestion`,
      GetReviewSuggestionResponseSchema
    ),

  /**
   * Force a fresh guest review suggestion. Backend queues it the same way
   * the review-window-open webhook does; poll getReviewSuggestion for it.
   * @param {string} reservationId - Reservation ID
   */
  regenerateReviewSuggestion: (reservationId: string) =>
    apiRequest(
      `/admin/reservations/${reservationId}/review-suggestion/regenerate`,
      RegenerateReviewSuggestionResponseSchema,
      {
        method: 'POST',
      }
    ),

  /**
   * Get the precomputed five-star confidence score for a reservation
   * (CAP-301). Returns { confidence: null } if generation hasn't run (or
   * finished) yet — same shape as getReviewSuggestion, but there is no
   * regenerate endpoint: this is a proactive, read-only precompute, not a
   * host-triggered draft.
   * @param {string} reservationId - Reservation ID
   */
  getReviewConfidence: (reservationId: string) =>
    apiRequest(
      `/admin/reservations/${reservationId}/review-confidence`,
      GetReviewConfidenceResponseSchema
    ),

  /**
   * The nightly outcome join behind the review-chase page's calibration
   * panel (CAP-301): tier hit-rates, AUC, reminder conversion, A/B arms,
   * confident misses, per-property priors. { calibration: null } before the
   * first nightly run.
   */
  /** Nightly review statistics for the Reviews page (admin-api services/review-stats.ts). */
  getReviewStats: () => apiRequest('/admin/reviews/stats', GetReviewStatsResponseSchema),

  /** The "Needs attention" to-do, open items first. */
  getReviewAttention: () =>
    apiRequest('/admin/reviews/attention', GetReviewAttentionResponseSchema),

  /** Mark an attention item open / done / skipped. */
  setReviewAttentionStatus: (id: string, status: AttentionStatus) =>
    apiRequest(
      `/admin/reviews/attention/${encodeURIComponent(id)}/status`,
      SetReviewAttentionStatusResponseSchema,
      { method: 'POST', body: JSON.stringify({ status }) }
    ),

  getReviewConfidenceCalibration: () =>
    apiRequest(
      '/admin/review-confidence/calibration',
      GetReviewConfidenceCalibrationResponseSchema
    ),

  /**
   * Get the latest AI-drafted thank-you message for a reservation (asks
   * the guest to review the host back). Returns { suggestion: null } if
   * none has been generated yet.
   * @param {string} reservationId - Reservation ID
   */
  getThankYouSuggestion: (reservationId: string) =>
    apiRequest(
      `/admin/reservations/${reservationId}/thank-you-suggestion`,
      GetThankYouSuggestionResponseSchema
    ),

  /**
   * Generate (or re-generate) a thank-you message. Synchronous — resolves
   * once the draft is ready (or failed), no polling needed.
   * @param {string} reservationId - Reservation ID
   */
  regenerateThankYouSuggestion: (reservationId: string) =>
    apiRequest(
      `/admin/reservations/${reservationId}/thank-you-suggestion/regenerate`,
      RegenerateThankYouSuggestionResponseSchema,
      {
        method: 'POST',
      }
    ),

  /**
   * Send a thank-you suggestion's text as a real guest message via
   * Uplisting. Never called automatically — always a direct operator
   * action (see root CLAUDE.md's "Reply suggestions: operators always
   * review before sending — system never auto-sends").
   * @param {string} reservationId - Reservation ID
   * @param {number} version - Suggestion version
   * @param {Object} params
   * @param {string} params.body - Final text to send (may differ from the
   *   suggestion's own text if the operator edited it)
   * @param {string} params.idempotencyKey - Caller-generated UUID, unique
   *   per Send click
   * @returns {Promise<{ sent: true, auditId: string, uplistingMessageId: string|null }>}
   */
  sendThankYouSuggestion: (
    reservationId: string,
    version: number,
    { body, idempotencyKey }: Pick<SendThankYouSuggestionRequest, 'body' | 'idempotencyKey'>
  ) =>
    apiRequest(
      `/admin/reservations/${reservationId}/thank-you-suggestion/${version}/send`,
      SendThankYouSuggestionResponseSchema,
      {
        method: 'POST',
        body: JSON.stringify({ body, idempotencyKey }),
      }
    ),

  /**
   * Deposit-eligible bookings with their server-derived deposit state (CAP-651).
   */
  getProtectionDeposits: (
    params: { window: 'upcoming' | 'past'; propertyId?: string; state?: string },
    signal?: AbortSignal
  ) => {
    const qs = new URLSearchParams({ window: params.window })
    if (params.propertyId) qs.set('propertyId', params.propertyId)
    if (params.state) qs.set('state', params.state)
    return apiRequest(`/admin/protection/deposits?${qs}`, ProtectionDepositsResponseSchema, {
      signal,
    })
  },

  /**
   * Every deposit setting in one read: switch, rules, channels, listings (CAP-651).
   */
  getProtectionSettings: (signal?: AbortSignal) =>
    apiRequest('/admin/protection/settings', ProtectionSettingsResponseSchema, { signal }),

  /**
   * Read the global security-deposit kill switch.
   */
  getSecurityDepositAutomation: (signal?: AbortSignal) =>
    apiRequest('/admin/security-deposit/automation', SecurityDepositAutomationSchema, { signal }),

  /**
   * Arm or disarm the global kill switch. Takes effect on the next sweep with
   * no deploy. The actor is taken from the caller's verified Access JWT
   * server-side, never from this payload.
   */
  setSecurityDepositAutomation: (
    enabled: boolean,
    options: { reason?: string; ifVersion?: number; signal?: AbortSignal } = {}
  ) =>
    apiRequest('/admin/security-deposit/automation', SecurityDepositAutomationSchema, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        enabled,
        ...(options.reason ? { reason: options.reason } : {}),
        // Required to arm; ignored for disarms, which are never version-checked
        // so the emergency stop can't be blocked.
        ...(enabled && options.ifVersion !== undefined ? { if_version: options.ifVersion } : {}),
      }),
      signal: options.signal,
    }),

  /**
   * Get a property's security-deposit amount config: the per-property override,
   * the code default, and the resolved (effective) amount.
   */
  getSecurityDepositConfig: (propertyId: string) =>
    apiRequest(
      `/admin/properties/${encodeURIComponent(propertyId)}/settings/security-deposit`,
      SecurityDepositConfigResponseSchema
    ),

  /**
   * Set a property's deposit amount. Pass null to clear the override and fall
   * back to the default.
   */
  updateSecurityDepositConfig: (propertyId: string, amountCents: number | null) =>
    apiRequest(
      `/admin/properties/${encodeURIComponent(propertyId)}/settings/security-deposit`,
      SecurityDepositConfigResponseSchema,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount_cents: amountCents }),
      }
    ),

  /**
   * Get a reservation's security-deposit state, plus a deep link into Stripe.
   * Capturing or refunding a deposit is done in the Stripe dashboard, not here.
   */
  getReservationSecurityDeposit: (reservationId: string) =>
    apiRequest(
      `/admin/reservations/${encodeURIComponent(reservationId)}/security-deposit`,
      ReservationSecurityDepositResponseSchema
    ),

  /**
   * Keep a booking's deposit hold live through an instant, or (null) put it back on
   * the normal release. For a stay extended outside the booking channel (CAP-1286).
   * Only records the instruction; the hourly sweep acts on it. A POST, not a PUT: the Access
   * CORS setting on the API host does not allow PUT, so a browser PUT is blocked.
   */
  setDepositHoldUntil: (
    reservationId: string,
    body: { hold_until: string | null; covers_reservation_id?: string | null; reason?: string }
  ) =>
    apiRequest(
      `/admin/reservations/${encodeURIComponent(reservationId)}/security-deposit/hold-until`,
      SetDepositHoldUntilResponseSchema,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }
    ),

  /**
   * Stripe activity feed: deposit holds, captures, releases, reconciliation,
   * failures, and inbound webhook events. Supersedes the removed
   * stripe-alerts feed, which only ever returned failed charges.
   */
  getStripeLogs: (
    options: {
      cursor?: string
      limit?: number
      propertyId?: string
      action?: string
      level?: string
      mode?: string
      from?: string
      to?: string
    } = {}
  ) => {
    const params = new URLSearchParams()
    for (const [key, value] of Object.entries(options)) {
      if (value !== undefined && value !== '') params.set(key, String(value))
    }
    const qs = params.toString()
    return apiRequest(`/admin/stripe-logs${qs ? `?${qs}` : ''}`, StripeLogsResponseSchema)
  },

  // --- Supplies (CAP-464) ---
  getSuppliesOverview: () => apiRequest('/admin/supplies/overview', SuppliesOverviewResponseSchema),
  getSuppliesTrends: () => apiRequest('/admin/supplies/trends', SuppliesTrendsResponseSchema),
  getPropertySupplies: (propertyId: string) =>
    apiRequest(
      `/admin/properties/${encodeURIComponent(propertyId)}/supplies`,
      PropertySuppliesResponseSchema
    ),
  /**
   * Where an original behind a receipt downloads from: the Worker streams it
   * as an attachment and the caller's Access session is the authorization,
   * so this is a plain link, never a bucket URL or a token.
   */
  receiptArtifactUrl: (receiptId: string, name: string) =>
    `${getApiBaseUrl()}/admin/receipts/${encodeURIComponent(receiptId)}/artifacts/${encodeURIComponent(name)}`,
  getReceipt: (receiptId: string) =>
    apiRequest(`/admin/receipts/${encodeURIComponent(receiptId)}`, GetReceiptResponseSchema),
  getUnmatchedReceipts: () =>
    apiRequest('/admin/receipts/unmatched?limit=200', UnmatchedReceiptsResponseSchema),
  /**
   * Receipts in one classification state (CAP-683), newest first. One page of
   * the largest size per state, asked separately so a long queue of waiting
   * receipts can never push the failed ones (and their re-run) off the page;
   * past 200 the page says there are more rather than paging through them.
   */
  getReceiptsByClassification: (status: 'pending' | 'failed') =>
    apiRequest(
      `/admin/receipts?classificationStatus=${status}&limit=200`,
      ListReceiptsResponseSchema
    ),
  /**
   * Ask again for every receipt with an unclassified line, which includes
   * every failed one (CAP-675). Rules and earlier answers fill in at once;
   * the rest goes back on the queue for the model.
   */
  reclassifyReceipts: () =>
    apiRequest('/admin/receipts/reclassify', ReclassifyReceiptsResponseSchema, {
      method: 'POST',
      body: JSON.stringify({ scope: 'unclassified' }),
    }),
  /** Pin a receipt to a house by hand (CAP-496); the resolver never overrides a manual pin. */
  attributeReceipt: (receiptId: string, propertyId: string) =>
    apiRequest(
      `/admin/receipts/${encodeURIComponent(receiptId)}/attribute`,
      AttributeReceiptResponseSchema,
      { method: 'POST', body: JSON.stringify({ property_id: propertyId }) }
    ),
  /** Skip a receipt by hand (CAP-497): no house, never listed as unmatched again; a later pin undoes it. */
  skipReceipt: (receiptId: string) =>
    apiRequest(
      `/admin/receipts/${encodeURIComponent(receiptId)}/skip`,
      AttributeReceiptResponseSchema,
      {
        method: 'POST',
      }
    ),
  getRestockSuggestions: (propertyId: string, adding: string[]) => {
    const params = new URLSearchParams({ propertyId })
    if (adding.length) params.set('adding', adding.join(','))
    return apiRequest(`/admin/supplies/restock?${params.toString()}`, RestockResponseSchema)
  },

  // --- Local model shadow (CAP-718) ---

  getLocalShadowSummary: (days: number) =>
    apiRequest(`/admin/local-shadow/summary?days=${days}`, LocalShadowSummarySchema),

  // --- Cleaning tracker configuration (CAP-372) ---

  getCleaningTeams: () => apiRequest('/admin/cleaning/teams', CleaningTeamsResponseSchema),

  createCleaningTeam: (team: Omit<CleaningTeam, 'created_at' | 'updated_at'>) =>
    apiRequest('/admin/cleaning/teams', CleaningTeamResponseSchema, {
      method: 'POST',
      body: JSON.stringify(team),
    }),

  /**
   * Partial update. Pass `active: false` to retire a crew — there is no delete,
   * by design.
   */
  updateCleaningTeam: (
    teamId: string,
    patch: Partial<Omit<CleaningTeam, 'id' | 'created_at' | 'updated_at'>>
  ) =>
    apiRequest(`/admin/cleaning/teams/${encodeURIComponent(teamId)}`, CleaningTeamResponseSchema, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  /** Global check-in/check-out times, plus the read-only state of both switches. */
  getCleaningSettings: () => apiRequest('/admin/cleaning/settings', CleaningSettingsResponseSchema),

  updateCleaningSettings: (patch: { checkin_time?: string; checkout_time?: string }) =>
    apiRequest('/admin/cleaning/settings', UpdateCleaningSettingsResponseSchema, {
      method: 'PUT',
      body: JSON.stringify(patch),
    }),

  getPropertyCleaning: (propertyId: string) =>
    apiRequest(
      `/admin/properties/${encodeURIComponent(propertyId)}/settings/cleaning`,
      PropertyCleaningResponseSchema
    ),

  /**
   * `rates` REPLACES the stored map rather than merging into it, so a removed
   * team's rate actually disappears. Send the whole matrix.
   */
  updatePropertyCleaning: (
    propertyId: string,
    patch: { enabled?: boolean; default_team_id?: string | null; rates?: Record<string, number> }
  ) =>
    apiRequest(
      `/admin/properties/${encodeURIComponent(propertyId)}/settings/cleaning`,
      z.object({ settings: PropertyCleaningSettingsSchema }),
      { method: 'PUT', body: JSON.stringify(patch) }
    ),
  // --- Cleaning tracker jobs (CAP-373 / CAP-374) ---

  /**
   * What needs doing, computed at read time from the open jobs. Never stored:
   * the retired sheet's `Alert` column was written by one script and read by
   * another, so it went stale, and a stale alert is worse than none.
   */
  /**
   * The Needs action list, one item per job (CAP-531). `kinds` selects jobs
   * carrying ANY of them, and returns each whole, with all its reasons.
   */
  getCleaningActionItems: (
    options: CleaningListFilters & { kinds?: readonly CleaningActionKind[] } = {}
  ) => {
    const params = new URLSearchParams()
    applyCleaningFilters(params, options)
    // Comma-separated rather than repeated, so the query string stays short
    // enough to read - admin-api accepts either spelling.
    if (options.kinds?.length) params.set('kind', options.kinds.join(','))
    const qs = params.toString()
    return apiRequest(
      `/admin/cleaning/actions-needed/jobs${qs ? `?${qs}` : ''}`,
      CleaningActionItemsResponseSchema
    )
  },

  /**
   * The text to send a crew: every job they have confirmed but not yet acked,
   * batched into one message (CAP-671) - the replacement for the per-job
   * "Cleaning changed" email, for a crew like Elisa's who gets texted rather
   * than emailed.
   */
  getCleaningCrewMessage: (teamId: string) =>
    apiRequest(
      `/admin/cleaning/crew-message?team=${encodeURIComponent(teamId)}`,
      CleaningCrewMessageResponseSchema
    ),

  /**
   * `open: true` drops finished jobs, which is what the upcoming list wants.
   *
   * `from`/`to` bound `scheduled_at`. A bare `YYYY-MM-DD` is compared against
   * the PROPERTY's own calendar day, inclusive at both ends; a value carrying a
   * time still compares as an instant. Pass days - the console only ever means
   * days, and a day is the thing that cannot land on the wrong side of
   * midnight.
   */
  getCleaningJobs: (
    options: CleaningListFilters & {
      open?: boolean
      statuses?: readonly CleaningJob['status'][]
      substates?: readonly NonNullable<CleaningJob['substate']>[]
      /**
       * Which end of time the page starts from (CAP-526).
       *
       * `desc` is how the board reaches BACKWARDS from today. Paging into the
       * past off an ascending list would mean computing an offset from a total
       * that moves whenever a job is derived, which repeats or drops a row
       * mid-scroll; asking for the page in descending order and rendering it
       * reversed is an ordinary forward page.
       */
      order?: 'asc' | 'desc'
    } = {}
  ) => {
    const params = new URLSearchParams()
    applyCleaningFilters(params, options)
    if (options.open) params.set('open', 'true')
    if (options.order) params.set('order', options.order)
    if (options.statuses?.length) params.set('status', options.statuses.join(','))
    if (options.substates?.length) params.set('substate', options.substates.join(','))
    const qs = params.toString()
    return apiRequest(`/admin/cleaning/jobs${qs ? `?${qs}` : ''}`, CleaningJobsResponseSchema)
  },

  /** One job, with the booking behind it (CAP-418). */
  getCleaningJob: (propertyId: string, jobId: string) =>
    apiRequest(
      `/admin/cleaning/jobs/${encodeURIComponent(propertyId)}/${encodeURIComponent(jobId)}`,
      CleaningJobDetailResponseSchema
    ),

  /**
   * The bytes of an ack screenshot (CAP-467).
   *
   * Fetched rather than put straight into an `<img src>`: admin-ui and the
   * API are different origins, and a plain `<img>` sends no cookies, so
   * Cloudflare Access would answer the login page and the image would simply
   * appear broken. Going through `fetch` with `credentials: "include"` is the
   * same path every other authenticated call takes.
   */
  getCleaningAckProof: async (proofId: string): Promise<Blob> => {
    const res = await fetch(
      `${getApiBaseUrl()}/admin/cleaning/ack-proof/${encodeURIComponent(proofId)}`,
      { credentials: 'include' }
    )
    if (!res.ok) throw new Error(`Could not load the ack screenshot (${res.status})`)
    return res.blob()
  },

  /**
   * Store a tapback screenshot and get its id (CAP-467). Raw bytes, not
   * multipart: there is one file and no other fields. The same image twice
   * returns the same id.
   */
  uploadCleaningAckProof: (image: Blob) =>
    apiRequest('/admin/cleaning/ack-proof', CleaningAckProofUploadSchema, {
      method: 'POST',
      body: image,
      headers: { 'Content-Type': image.type || 'application/octet-stream' },
    }),

  /** Crew asks, newest first (CAP-709). `status: ['waiting']` is the board's list. */
  listCleaningAsks: (options: { status?: readonly CleaningAskStatus[]; team?: string } = {}) => {
    const params = new URLSearchParams()
    if (options.status?.length) params.set('status', options.status.join(','))
    if (options.team) params.set('team', options.team)
    const qs = params.toString()
    return apiRequest(`/admin/cleaning/asks${qs ? `?${qs}` : ''}`, CleaningAsksResponseSchema)
  },

  /**
   * Record that the crew message was sent. `message` is the exact text copied;
   * a 409 means the jobs changed since and it has to be copied again.
   */
  createCleaningAsk: (team: string, message: string) =>
    apiRequest('/admin/cleaning/asks', CleaningAskResponseSchema, {
      method: 'POST',
      body: JSON.stringify({ team, message }),
    }),

  /**
   * The crew's tapback: acks every job in the ask, or none. A 409's body
   * carries `problems`, one per job that changed since it was asked.
   */
  answerCleaningAsk: (askId: string, body: { proof_id: string; reacted_at?: string }) =>
    apiRequest(
      `/admin/cleaning/asks/${encodeURIComponent(askId)}/answer`,
      CleaningAskAnswerResponseSchema,
      { method: 'POST', body: JSON.stringify(body) }
    ),

  /** No reply is coming: stop showing the ask as waiting. */
  closeCleaningAsk: (askId: string) =>
    apiRequest(
      `/admin/cleaning/asks/${encodeURIComponent(askId)}/close`,
      CleaningAskResponseSchema,
      {
        method: 'POST',
        body: JSON.stringify({}),
      }
    ),

  /** That job's timeline, from its own timestamps. */
  getCleaningJobHistory: (propertyId: string, jobId: string) =>
    apiRequest(
      `/admin/cleaning/jobs/${encodeURIComponent(propertyId)}/${encodeURIComponent(jobId)}/history`,
      CleaningJobHistoryResponseSchema
    ),

  /**
   * The operator actions, one endpoint each. The body differs per action:
   * `assign` takes `team_id`, `reschedule` a `scheduled_at`, `decide` a
   * `needs_cleaning`, `note` a `note`; the rest take none.
   */
  runCleaningJobAction: (
    propertyId: string,
    jobId: string,
    action:
      | 'assign'
      | 'ack'
      | 'confirm'
      | 'not-required'
      | 'undo-not-required'
      | 'decide'
      | 'reschedule'
      | 'told-them'
      | 'pin'
      | 'unpin'
      | 'note'
      | 'undo',
    body: Record<string, unknown> = {}
  ) =>
    apiRequest(
      `/admin/cleaning/jobs/${encodeURIComponent(propertyId)}/${encodeURIComponent(jobId)}/${action}`,
      CleaningJobResponseSchema,
      { method: 'POST', body: JSON.stringify(body) }
    ),

  // --- Massachusetts room occupancy, Form ROC (CAP-545..CAP-548) ---

  getMaTaxRegistration: (propertyId: string) =>
    apiRequest(
      `/admin/properties/${encodeURIComponent(propertyId)}/settings/ma-tax-registration`,
      MaTaxRegistrationResponseSchema
    ),

  /** REPLACES the record: send every field; one left out is stored as null. */
  updateMaTaxRegistration: (
    propertyId: string,
    body: Omit<MaTaxRegistration, 'property_id' | 'updated_at' | 'updated_by'>
  ) =>
    apiRequest(
      `/admin/properties/${encodeURIComponent(propertyId)}/settings/ma-tax-registration`,
      MaTaxRegistrationResponseSchema,
      { method: 'PUT', body: JSON.stringify(body) }
    ),

  getMaTaxMunicipalities: () =>
    apiRequest('/admin/ma-tax/municipalities', MaTaxMunicipalitiesResponseSchema),

  getRocPreview: (period: string) =>
    apiRequest(`/admin/ma-tax/roc/preview?period=${encodeURIComponent(period)}`, RocReportSchema),

  /** Recomputes on the server; a blocked month comes back as an error. */
  createRocVersion: (body: {
    period: string
    excluded_property_ids: string[]
    acknowledgments: Record<string, string>
  }) =>
    apiRequest('/admin/ma-tax/roc/versions', CreateRocVersionResponseSchema, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  listRocVersions: (period?: string) =>
    apiRequest(
      `/admin/ma-tax/roc/versions${period ? `?period=${encodeURIComponent(period)}` : ''}`,
      RocVersionsResponseSchema
    ),

  markRocVersionSubmitted: (id: string, confirmationNumber?: string) =>
    apiRequest(
      `/admin/ma-tax/roc/versions/${encodeURIComponent(id)}/submitted`,
      z.object({ version: RocVersionSchema }),
      {
        method: 'POST',
        body: JSON.stringify(confirmationNumber ? { confirmation_number: confirmationNumber } : {}),
      }
    ),

  listMaTaxConfirmations: (period?: string) =>
    apiRequest(
      `/admin/ma-tax/confirmations${period ? `?period=${encodeURIComponent(period)}` : ''}`,
      MaTaxConfirmationsResponseSchema
    ),

  matchMaTaxConfirmation: (confirmationNumber: string, versionId: string) =>
    apiRequest(
      `/admin/ma-tax/confirmations/${encodeURIComponent(confirmationNumber)}/match`,
      z.object({ confirmation: MaTaxConfirmationSchema }),
      { method: 'POST', body: JSON.stringify({ version_id: versionId }) }
    ),

  getOwnerEmailPreviews: (versionId: string) =>
    apiRequest(
      `/admin/ma-tax/roc/versions/${encodeURIComponent(versionId)}/owner-emails`,
      OwnerEmailPreviewsResponseSchema
    ),

  /** Emails owners. Only the approval button in the Tax reports page calls this. */
  sendOwnerEmails: (versionId: string, propertyIds: string[]) =>
    apiRequest(
      `/admin/ma-tax/roc/versions/${encodeURIComponent(versionId)}/owner-emails/send`,
      SendOwnerEmailsResponseSchema,
      { method: 'POST', body: JSON.stringify({ property_ids: propertyIds }) }
    ),

  /** A plain link: the browser's Access session authorizes the download. */
  rocVersionFileUrl: (id: string, part: 'xlsx' | 'calculation') =>
    `${getApiBaseUrl()}/admin/ma-tax/roc/versions/${encodeURIComponent(id)}/${part}`,

  // --- Owner onboarding (CAP-1457 in admin-api, CAP-1467 here) ---

  /**
   * Start onboarding: the local property, its empty profile, the owner's
   * portal user and the onboarding email. Idempotent on email plus address: a
   * repeat answers `created: false` and sends nothing, unless no email ever
   * reached the owner, in which case it is the retry. 409 `onboarding_finished`
   * once the property is linked or archived.
   */
  startOnboarding: (body: StartOnboardingRequest) =>
    apiRequest('/admin/onboarding', StartOnboardingResponseSchema, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** Every property still onboarding, with its profile progress (oldest invite first). */
  getOnboarding: () => apiRequest('/admin/onboarding', OnboardingListResponseSchema),

  /**
   * Re-send the onboarding email to the property's owners; manual only. A 200
   * can still carry `errors[]` for owners it could not reach.
   */
  nudgeOnboarding: (propertyId: string) =>
    apiRequest(
      `/admin/onboarding/${encodeURIComponent(propertyId)}/nudge`,
      OnboardingNudgeResponseSchema,
      { method: 'POST' }
    ),

  // --- Property profile (CAP-1458 in admin-api, CAP-1467 here) ---

  /**
   * The profile and its completeness; the empty, unsaved profile before any
   * save, with `listing_diff` on live properties (where Uplisting and the
   * owner disagree).
   */
  getPropertyProfile: (propertyId: string) =>
    apiRequest(
      `/admin/properties/${encodeURIComponent(propertyId)}/profile`,
      PropertyProfileResponseSchema
    ),

  /**
   * An ops correction of one whole section, stamped `source: ops` on the
   * leaves it changes. 409 `section_conflict` (with the current section) when
   * someone saved it after `expected_updated_at`.
   */
  savePropertyProfileSection: (
    propertyId: string,
    sectionId: PropertyProfileSectionId,
    body: { expected_updated_at: string | null; fields: Record<string, unknown> }
  ) =>
    apiRequest(
      `/admin/properties/${encodeURIComponent(propertyId)}/profile/sections/${sectionId}`,
      PropertyProfileResponseSchema,
      { method: 'PUT', body: JSON.stringify(body) }
    ),

  /** Mark reviewed: clears every "changed since review". 409 while the profile is still a draft. */
  reviewPropertyProfile: (propertyId: string) =>
    apiRequest(
      `/admin/properties/${encodeURIComponent(propertyId)}/profile/review`,
      PropertyProfileResponseSchema,
      { method: 'POST' }
    ),

  /** One section's save history, newest first. */
  getPropertyProfileRevisions: (
    propertyId: string,
    sectionId: PropertyProfileSectionId,
    cursor?: string | null
  ) => {
    const params = new URLSearchParams({ section: sectionId })
    if (cursor) params.set('cursor', cursor)
    return apiRequest(
      `/admin/properties/${encodeURIComponent(propertyId)}/profile/revisions?${params}`,
      AdminPropertyProfileRevisionsResponseSchema
    )
  },

  // --- Property documents and photos (CAP-1459 in admin-api, CAP-1467 here) ---

  getPropertyDocuments: (propertyId: string) =>
    apiRequest(
      `/admin/properties/${encodeURIComponent(propertyId)}/documents`,
      PropertyDocumentsListResponseSchema
    ),

  /**
   * An ops upload (multipart: `file`, `kind`, optional `section_id`), stored as
   * `source: ops`. A refusal (413 too large, 415 type, 409 limit) carries
   * `{ code, message }` in the thrown error's `body`, shown as written.
   */
  uploadPropertyDocument: (
    propertyId: string,
    file: File,
    kind: PropertyDocumentKind,
    sectionId: PropertyProfileSectionId | null
  ) => {
    const form = new FormData()
    form.append('file', file)
    form.append('kind', kind)
    if (sectionId) form.append('section_id', sectionId)
    return apiRequest(
      `/admin/properties/${encodeURIComponent(propertyId)}/documents`,
      PropertyDocumentUploadResponseSchema,
      { method: 'POST', body: form }
    )
  },

  /**
   * A plain link: the browser's Access session authorizes the download, and
   * admin-api answers `Content-Disposition: attachment`, so it saves and is
   * never shown inline. Nothing in the URL grants access by itself.
   */
  propertyDocumentDownloadUrl: (propertyId: string, documentId: string) =>
    `${getApiBaseUrl()}/admin/properties/${encodeURIComponent(propertyId)}/documents/${encodeURIComponent(documentId)}`,

  /**
   * Link a local onboarding property to its Uplisting listing (CAP-1457).
   * `dryRun` answers the moves it would make and writes nothing. A refusal
   * (409 `move_conflict`, `already_linked`, `target_archived`, ...) carries
   * `{ error, message, conflicts }` in the thrown error's `body`.
   */
  linkUplisting: (localPropertyId: string, uplistingPropertyId: string, dryRun: boolean) =>
    apiRequest(
      `/admin/properties/${encodeURIComponent(localPropertyId)}/link-uplisting`,
      LinkUplistingResponseSchema,
      {
        method: 'POST',
        body: JSON.stringify({ uplisting_property_id: uplistingPropertyId, dry_run: dryRun }),
      }
    ),
}
